// frontend/src/components/TripTracker.js

import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import axios from '../axiosConfig';
import { AlertTriangle, Check, Navigation2, Clock, Users, Briefcase, User, Car, Calendar, BookOpen, Map, QrCode, ListOrdered } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useTripTracking, GPS_FEED_FRESHNESS_MS } from '../hooks/useTripTracking';
import { useETADisplayData } from '../hooks/useETADisplayData';
import { useRelevantStops } from '../hooks/useRelevantStops';
import { useWaypointTranslations } from '../hooks/useWaypointTranslations';
import { getTranslatedWaypointName } from '../utils/etaWaypointHelpers';
import { isTripEntityMismatch } from '../utils/entityMatch';
import TripTrackingMap from './TripTrackingMap';
import RiderTripSummary from './rider/RiderTripSummary';

import CancelConfirmDialog from './dialogs/CancelConfirmDialog';
import StartConfirmDialog from './dialogs/StartConfirmDialog';
import CompleteConfirmDialog from './dialogs/CompleteConfirmDialog';
import ReferenceRouteDialog from './dialogs/ReferenceRouteDialog';
import SaveShortcutSegmentsDialog from './dialogs/SaveShortcutSegmentsDialog';
import TripWaypointReorderModal from './dialogs/TripWaypointReorderModal';
import OperationalDelayDialog from './dialogs/OperationalDelayDialog';
import WaypointCorrectionDialog from './ETADisplay/WaypointCorrectionDialog';
import shortcutSegmentService from '../services/shortcutSegmentService';
import ETADisplay from './ETADisplay';
import BoardingDisplay from './BoardingDisplay';
import BoardingStatusDashboard from './boarding/BoardingStatusDashboard';
import BoardingPassModal from './boarding/BoardingPassModal';
import etaService from '../services/etaService';
import {
  formatTime,
  formatDate,
  formatTimeString,
  parseBackendUtcInstant
} from '../utils/dateTimeFormatter';
import {
  buildEntityScheduledDepartureDate,
  resolveEntityTimeZone
} from '../utils/entityTimeZone';
import { hasRoleLevel, isAdmin as checkIsAdmin } from '../utils/roleUtils';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';
import { useTheme } from '../contexts/ThemeContext';
import { usePreferences } from '../contexts/PreferenceContext';
import manualWaypointService from '../services/manualWaypointService';
import { getTripDisplayStops } from '../utils/tripDisplayStops';
import {
  createCanonicalLiveEtaPayload,
  deriveLiveEtaPayload,
  isNoDataEtaPayload
} from '../utils/liveEta';
import {
  getSelectedRiderBooking,
  normalizeRiderBookings,
  projectSelectedRiderBookingOntoTrip,
  selectDefaultRiderBooking
} from '../utils/riderTripSummary';
import logger, { getSafeErrorDetails } from '../utils/logger';
import { canLaunchWaypointReorder } from '../utils/waypointReorderAccess';

const unwrapAxiosPayload = (response) => {
  if (!response || typeof response !== 'object') {
    return response;
  }

  const container = response.data;
  if (container && typeof container === 'object' && Object.prototype.hasOwnProperty.call(container, 'data')) {
    return container.data;
  }

  return container;
};

const buildWaypointReorderRefreshKey = (payload) => {
  const changeType = payload?.changeType || payload?.type;
  const version = payload?.newVersion ?? payload?.version;

  if (!changeType || version === undefined || version === null) {
    return null;
  }

  return `${changeType}:${version}`;
};

const getSafeErrorContext = (error, context = {}) => ({
  ...(getSafeErrorDetails(error) || {}),
  ...context,
});

const isOperationalDelayProjectionStale = (error) => {
  const payload = error?.response?.data || error?.responseBody || {};
  return error?.response?.status === 409
    && payload.code === 'OPERATIONAL_DELAY_PROJECTION_STALE';
};

const getTripLogSummary = (trip) => ({
  tripId: trip?.id ?? trip?.trip_id ?? null,
  status: trip?.status || null,
  routeId: trip?.route_id ?? null,
  stopCount: Array.isArray(trip?.stops) ? trip.stops.length : 0,
  effectiveStopCount: Array.isArray(trip?.effective_stops) ? trip.effective_stops.length : 0,
  selectedArchivedType: trip?.selected_archived_type || null,
  hasUserBooking: Boolean(trip?.user_booking),
  userBookingCount: Array.isArray(trip?.user_bookings) ? trip.user_bookings.length : 0,
});

const getEntityLogSummary = (entity) => ({
  entityId: entity?.entityId ?? null,
  userRole: entity?.userRole || null,
});

const getLocationLogSummary = (location) => ({
  hasLocation: Boolean(location),
  hasTimestamp: Boolean(location?.timestamp),
  hasSpeed: Number.isFinite(Number(location?.speed)),
  hasHeading: Number.isFinite(Number(location?.heading)),
});

const getEtaLogSummary = (etaPayload) => ({
  cached: Boolean(etaPayload?.cached),
  provider: etaPayload?.provider || null,
  waypointCount: Array.isArray(etaPayload?.waypoints) ? etaPayload.waypoints.length : 0,
  hasTimestamp: Boolean(etaPayload?.timestamp),
  activeEtaState: etaPayload?.active_eta_state || null,
});

const ETA_DISABLED_REASON = 'eta_disabled_by_entity_preference';

const isDistanceOnlyEtaPayload = (payload = null) => (
  payload?.provider === 'none'
  || payload?.eta_disabled_reason === ETA_DISABLED_REASON
);

const shouldRejectEtaForNoneSource = (effectiveEtaSource, payload = null) => (
  effectiveEtaSource === 'none'
  && Boolean(payload)
  && !isDistanceOnlyEtaPayload(payload)
);

const clearPreTripStaticEstimate = (etaState = null) => {
  if (!etaState || (
    !Object.prototype.hasOwnProperty.call(etaState, 'estimated')
    && !Object.prototype.hasOwnProperty.call(etaState, 'estimatedEtaDisabled')
    && !Object.prototype.hasOwnProperty.call(
      etaState,
      'estimatedOperationalDelayDeclarations'
    )
    && !Object.prototype.hasOwnProperty.call(
      etaState,
      'estimatedOperationalDelayProjectionWaypointOrderVersion'
    )
  )) {
    return etaState;
  }

  const nextEtaState = { ...etaState };
  delete nextEtaState.estimated;
  delete nextEtaState.estimatedEtaDisabled;
  delete nextEtaState.estimatedOperationalDelayDeclarations;
  delete nextEtaState.estimatedOperationalDelayProjectionWaypointOrderVersion;
  return Object.keys(nextEtaState).length > 0 ? nextEtaState : null;
};

const normalizeEstimatedRouteProjection = (projection = null) => {
  const isProjectionEnvelope = projection
    && typeof projection === 'object'
    && Object.prototype.hasOwnProperty.call(projection, 'estimatedData');
  const hasOperationalDelayProjection = isProjectionEnvelope
    && Array.isArray(projection.operationalDelayDeclarations)
    && Object.prototype.hasOwnProperty.call(
      projection,
      'operationalDelayProjectionWaypointOrderVersion'
    );
  return {
    estimatedData: isProjectionEnvelope ? projection.estimatedData : projection,
    hasOperationalDelayProjection,
    operationalDelayDeclarations: hasOperationalDelayProjection
      ? projection.operationalDelayDeclarations
      : [],
    operationalDelayProjectionWaypointOrderVersion: hasOperationalDelayProjection
      ? projection.operationalDelayProjectionWaypointOrderVersion
      : undefined
  };
};

const buildEstimatedRouteState = (projection = null) => {
  const normalized = normalizeEstimatedRouteProjection(projection);
  const state = { estimated: normalized.estimatedData };
  // Distance-only hydration returns only estimatedData. Do not manufacture an
  // empty declarations field or undefined version at this ownership boundary;
  // the Delay tuple exists only when both members came from the same response.
  if (normalized.hasOperationalDelayProjection) {
    state.estimatedOperationalDelayDeclarations =
      normalized.operationalDelayDeclarations;
    state.estimatedOperationalDelayProjectionWaypointOrderVersion =
      normalized.operationalDelayProjectionWaypointOrderVersion;
  }
  return state;
};

const OPERATIONAL_DELAY_DERIVED_FIELDS = [
  'cumulative_arrival_delay_minutes',
  'cumulative_departure_delay_minutes',
  'eta_minutes_with_operational_delay',
  'boundary_time_with_operational_delay'
];

const stripOperationalDelayFromWaypoint = (waypoint = {}) => {
  const rawWaypoint = { ...waypoint };
  OPERATIONAL_DELAY_DERIVED_FIELDS.forEach((field) => delete rawWaypoint[field]);
  return rawWaypoint;
};

/**
 * Keep the historical route rows while removing only derived Delay authority.
 * A committed Clear, passage, correction, or reorder can make those four
 * fields stale before its replacement estimate arrives; retaining raw route
 * duration avoids blanking the user's fallback if that refresh fails.
 */
const stripOperationalDelayFromEstimatedState = (etaState = null) => {
  if (!etaState) return etaState;
  const estimated = etaState?.estimated;
  if (!estimated) {
    if (
      !Object.prototype.hasOwnProperty.call(
        etaState,
        'estimatedOperationalDelayDeclarations'
      )
      && !Object.prototype.hasOwnProperty.call(
        etaState,
        'estimatedOperationalDelayProjectionWaypointOrderVersion'
      )
    ) return etaState;
    const withoutDeclarations = { ...etaState };
    delete withoutDeclarations.estimatedOperationalDelayDeclarations;
    delete withoutDeclarations.estimatedOperationalDelayProjectionWaypointOrderVersion;
    return Object.keys(withoutDeclarations).length > 0 ? withoutDeclarations : null;
  }

  const strippedEstimate = Array.isArray(estimated)
    ? estimated.map(stripOperationalDelayFromWaypoint)
    : {
        ...estimated,
        waypoints: Array.isArray(estimated.waypoints)
          ? estimated.waypoints.map(stripOperationalDelayFromWaypoint)
          : estimated.waypoints
      };
  const nextEtaState = {
    ...etaState,
    estimated: strippedEstimate
  };
  delete nextEtaState.estimatedOperationalDelayDeclarations;
  delete nextEtaState.estimatedOperationalDelayProjectionWaypointOrderVersion;
  return nextEtaState;
};

const GPS_START_ERROR_KEYS = {
  GPS_SHARING_OFF: 'gpsSource.errors.sharingOff',
  GPS_SOURCE_REQUIRED: 'gpsSource.errors.sourceRequired',
  GPS_SOURCE_UNAVAILABLE: 'gpsSource.errors.sourceUnavailable',
  GPS_SOURCE_VEHICLE_REQUIRED: 'gpsSource.errors.sourceUnavailable',
  GPS_SOURCE_VEHICLE_INVALID: 'gpsSource.errors.sourceUnavailable',
  GPS_DEVICE_UNAVAILABLE: 'gpsSource.errors.sourceUnavailable',
  GPS_DEFAULT_DEVICE_UNAVAILABLE: 'gpsSource.errors.sourceUnavailable',
  GPS_FEED_INACTIVE: 'gpsSource.errors.feedInactive',
  GPS_FEED_STATE_UNAVAILABLE: 'gpsSource.errors.feedStateUnavailable'
};
const AUTO_DRIVER_ASSIGNMENT_SOURCES = new Set(['auto_booking', 'auto_single']);

const getPlanLogSummary = (plan, source) => ({
  hasPlan: Boolean(plan),
  segmentCount: Array.isArray(plan?.segments) ? plan.segments.length : 0,
  waypointCount: Array.isArray(plan?.waypoints) ? plan.waypoints.length : 0,
  source,
});

const getWaypointEventLogSummary = (payload) => ({
  waypointId: payload?.waypointId ?? payload?.waypoint_id ?? null,
  state: payload?.state || null,
  reason: payload?.reason || null,
  changeType: payload?.changeType || payload?.type || null,
  newVersion: payload?.newVersion ?? payload?.version ?? null,
  syncStatus: payload?.sync?.status || null,
});

// Trip Action Buttons Component
// Renders action buttons based on trip status
function TripActions({ status, onStartTrip, onCompleteTrip, onCancelTrip, onCreateReferenceRoute, isAdmin, isCompletingTrip = false }) {
  const { t } = useTranslation();
  const { components, cn } = useTheme();

  // Common button styles
  const greenBtn = "px-4 py-2 rounded bg-green-500 text-white hover:bg-green-600";

  if (status === 'dispatched') {
    return (
      <div className="flex items-center gap-2">
        <button onClick={onStartTrip} className={cn("px-4 py-2 rounded", components.button.primary)}>
          {t('startTrip')}
        </button>
        <button onClick={onCancelTrip} className={cn("px-4 py-2 rounded", components.button.danger)}>
          {t('cancelTrip')}
        </button>
      </div>
    );
  }

  if (status === 'in_progress') {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={onCompleteTrip}
          disabled={isCompletingTrip}
          className={cn(greenBtn, 'disabled:opacity-50 disabled:cursor-not-allowed')}
        >
          {t('completeTrip')}
        </button>
        <button onClick={onCancelTrip} className={cn("px-4 py-2 rounded", components.button.danger)}>
          {t('cancelTrip')}
        </button>
      </div>
    );
  }

  if (status === 'scheduled') {
    return (
      <div className="flex items-center gap-2">
        <button onClick={onStartTrip} className={cn("px-4 py-2 rounded", components.button.primary)}>
          {t('startTrip')}
        </button>
        <button onClick={onCancelTrip} className={cn("px-4 py-2 rounded", components.button.danger)}>
          {t('cancelTrip')}
        </button>
      </div>
    );
  }

  if (status === 'completed' && isAdmin) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={onCreateReferenceRoute}
          className={cn("px-4 py-2 rounded bg-purple-500 text-white hover:bg-purple-600 flex items-center gap-2")}
        >
          <Map className="h-4 w-4" />
          {t('createReferenceRoute')}
        </button>
      </div>
    );
  }

  return null;
}

// Keep summary-specific ETA hooks inside the mounted rider section. Driver and
// admin views still render ETADisplay, so running the summary hooks above the
// visibility gate would duplicate hot-path ETA row processing for hidden UI.
const RiderTripSummarySection = ({
  displayEtaData,
  connectionStatus,
  isStalePresentation,
  isAwaitingLiveEtaResume,
  riderScopedTripData,
  gpsActive,
  isDriver,
  userPickupStopId,
  userDropoffStopId,
  currentTripPlan,
  showWaypoints,
  waypointStates,
  entitySettings,
  riderBookings,
  selectedRiderBooking,
  onSelectBooking,
  riderContentInsetClassName,
  entityTimeZone
}) => {
  const riderSummaryDisplayData = useETADisplayData(
    displayEtaData,
    riderScopedTripData,
    gpsActive,
    isDriver,
    userPickupStopId,
    userDropoffStopId,
    currentTripPlan,
    entityTimeZone
  );

  const riderSummaryRelevantStops = useRelevantStops(
    riderSummaryDisplayData,
    isDriver,
    userPickupStopId,
    userDropoffStopId,
    riderScopedTripData,
    showWaypoints,
    displayEtaData
  );

  return (
    <div className={riderContentInsetClassName}>
      <RiderTripSummary
        tripData={riderScopedTripData}
        displayData={riderSummaryDisplayData}
        relevantStops={riderSummaryRelevantStops}
        waypointStates={waypointStates}
        entitySettings={entitySettings}
        riderBookings={riderBookings}
        selectedBooking={selectedRiderBooking}
        onSelectBooking={onSelectBooking}
        entityTimeZone={entityTimeZone}
        connectionStatus={connectionStatus}
        isStalePresentation={isStalePresentation}
        isAwaitingLiveEtaResume={isAwaitingLiveEtaResume}
      />
    </div>
  );
};

// TripTracker component
const TripTracker = ({
  tripId,
  tripData: initialTripData,
  onStartTrip,
  onCompleteTrip,
  onCancelTrip,
  onTripStatusChange,
  currentEntity,
  isDriver = false,
  isCompletingTrip = false,
}) => {
  const { t } = useTranslation();
  const { background, text, components, shadow, cn } = useTheme();
  const { entitySettings } = usePreferences();
  const effectiveEtaSource = entitySettings?.eta_source || currentEntity?.eta_source || null;
  const [tripData, setTripData] = useState(initialTripData);
  const [etaData, setEtaData] = useState(null);
  const [usePhoneGPS, setUsePhoneGPS] = useState(false);
  const [gpsSourceOptions, setGpsSourceOptions] = useState(null);
  const [gpsSourceOptionsScope, setGpsSourceOptionsScope] = useState(null);
  const [gpsSourceOptionsLoading, setGpsSourceOptionsLoading] = useState(false);
  const [gpsSourceOptionsError, setGpsSourceOptionsError] = useState(null);
  const [showGpsSourceChooser, setShowGpsSourceChooser] = useState(false);
  const [pendingGpsSourceValue, setPendingGpsSourceValue] = useState(null);
  const [isGpsSourceSwitching, setIsGpsSourceSwitching] = useState(false);
  const [gpsSourceConflict, setGpsSourceConflict] = useState(null);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [startVehicleOptions, setStartVehicleOptions] = useState([]);
  const [selectedStartVehicleId, setSelectedStartVehicleId] = useState(null);
  const [startVehicleConflict, setStartVehicleConflict] = useState(null);
  const [startVehicleSelectionRequired, setStartVehicleSelectionRequired] = useState(false);
  const [pendingStartArchivedType, setPendingStartArchivedType] = useState(null);
  const [pendingGpsShareSelectionValue, setPendingGpsShareSelectionValue] = useState(null);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showReferenceRouteDialog, setShowReferenceRouteDialog] = useState(false);
  const [referenceRouteLoading, setReferenceRouteLoading] = useState(false);
  const [showShortcutSegmentsDialog, setShowShortcutSegmentsDialog] = useState(false);
  const [shortcutEdgesData, setShortcutEdgesData] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [showOperationalDelayDialog, setShowOperationalDelayDialog] = useState(false);
  const [locationInfo, setLocationInfo] = useState({
    speed: 0,
    heading: 0,
    lastUpdate: null,
    lastUpdateInvalid: false,
  });
  const [waypointStates, setWaypointStates] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastWaypointTimestamp, setLastWaypointTimestamp] = useState(null);
  const [nextExpectedWaypoint, setNextExpectedWaypoint] = useState(null);
  const [lastCheckedLocation, setLastCheckedLocation] = useState(null);
  const [boardingPassModalOpen, setBoardingPassModalOpen] = useState(false);
  const [selectedBoardingBooking, setSelectedBoardingBooking] = useState(null);
  const [selectedRiderBookingId, setSelectedRiderBookingId] = useState(null);
  const [hasManualRiderBookingSelection, setHasManualRiderBookingSelection] = useState(false);
  const [showWaypoints, setShowWaypoints] = useState(false);
  const [isCompleteActionPending, setIsCompleteActionPending] = useState(false);
  const [planRefreshTrigger, setPlanRefreshTrigger] = useState(0);
  const [boardingStatusRefreshTrigger, setBoardingStatusRefreshTrigger] = useState(0);
  const [latestPlanUpdate, setLatestPlanUpdate] = useState(null);
  const [currentTripPlan, setCurrentTripPlan] = useState(null);
  const [liveEtaNowTick, setLiveEtaNowTick] = useState(Date.now());
  // Rider location-feed observation. The client's own receipt time is used so a
  // skewed device clock on the reporting side cannot fake freshness.
  const lastLiveLocationClientMsRef = useRef(null);
  const [riderFeedStale, setRiderFeedStale] = useState(false);
  // Whether any live position has been observed for this trip. Staleness can
  // only be asserted against an actual prior observation.
  const [hasObservedLiveLocation, setHasObservedLiveLocation] = useState(false);
  // One-shot immutable snapshot of the derived live ETA, captured when the feed
  // goes stale. `null` means "not frozen"; a wrapper object is stored so a
  // snapshot with no payload still counts as frozen and cannot be recomputed.
  const [frozenLiveEtaSnapshot, setFrozenLiveEtaSnapshot] = useState(null);
  const frozenLiveEtaSnapshotRef = useRef(null);
  // Recovery bookkeeping: the client receipt time of the live_location that
  // ended staleness. The frozen snapshot stays displayed until the first ETA
  // accepted strictly after that receipt.
  const feedRecoveryReceiptMsRef = useRef(null);
  const [isAwaitingLiveEtaResume, setIsAwaitingLiveEtaResume] = useState(false);
  const [correctionDialogState, setCorrectionDialogState] = useState({
    isOpen: false,
    waypointId: null,
    waypointName: '',
    coLocatedWaypoints: []
  });
  const [isCorrectionDialogLoading, setIsCorrectionDialogLoading] = useState(false);
  const correctionSelectionResolverRef = useRef(null);
  const manualSkipRefreshInFlightRef = useRef(null);
  const preTripPlanRefreshInFlightRef = useRef(null);
  const planUpdateSequenceRef = useRef(0);
  const requestedPreTripPlanUpdateTokenRef = useRef(null);
  const queuedPreTripPlanUpdateTokenRef = useRef(null);
  const completedPreTripPlanUpdateTokenRef = useRef(null);
  const reorderRefreshInFlightRef = useRef(null);
  const reorderRefreshKeyRef = useRef(null);
  const queuedReorderRefreshPayloadRef = useRef(null);
  const completedReorderRefreshKeyRef = useRef(null);
  const completeActionInFlightRef = useRef(false);
  const gpsSourceSwitchInFlightRef = useRef(false);
  const committedEtaSourceRef = useRef(effectiveEtaSource);
  const gpsSourceOptionsRequestRef = useRef({ token: 0, entityId: null, tripId: null });
  const gpsSourceOptionsRenderScopeRef = useRef({
    entityId: currentEntity?.entityId ?? null,
    tripId: tripId ?? null,
    loadAllowed: false
  });
  const fetchEstimatedRouteDurationRef = useRef(null);
  const delayRefreshPendingRef = useRef(new globalThis.Map());
  const delayRefreshSequenceRef = useRef(0);
  const etaContinuationGenerationRef = useRef(0);
  const bookingRefreshSequenceRef = useRef(0);
  const delayRefreshScopeRef = useRef({ entityId: null, tripId: null });
  const waitingEtaHydrationTimerRef = useRef(null);
  const waitingEtaHydrationRetryStateRef = useRef({
    key: null,
    attempts: 0
  });
  const tripStartEtaStateRef = useRef({
    tripId: null,
    state: null,
    updatedAt: 0
  });
  delayRefreshScopeRef.current = {
    entityId: currentEntity?.entityId ?? null,
    tripId: tripId ?? null
  };

  // Hook to trigger re-render when date/time preferences change
  const { dateFormat, timeFormat, distanceUnit } = useDateTimePreferences();

  // Create a ref to store the sendStatusUpdate function
  const sendStatusUpdateRef = useRef(null);

  // Ref to track current trip status for reliable comparison in callbacks
  // (avoids depending on React state updater timing)
  const tripStatusRef = useRef(tripData?.status);
  useEffect(() => {
    tripStatusRef.current = tripData?.status;
  }, [tripData?.status]);

  // Async hydration may settle after React has rendered a newer source. Update
  // this authority only after that render commits: a speculative render must
  // not authorize or reject a response for UI state the user never received.
  useLayoutEffect(() => {
    committedEtaSourceRef.current = effectiveEtaSource;
  }, [effectiveEtaSource]);

  useLayoutEffect(() => () => {
    ++etaContinuationGenerationRef.current;
    ++bookingRefreshSequenceRef.current;
    ++delayRefreshSequenceRef.current;
  }, [currentEntity?.entityId, tripId]);

  const beginOperationalDelayStaticRefresh = useCallback(() => {
    const entityId = currentEntity?.entityId;
    const scopedTripId = tripId;
    const sequence = delayRefreshSequenceRef.current + 1;
    delayRefreshSequenceRef.current = sequence;
    setEtaData(stripOperationalDelayFromEstimatedState);
    return {
      ownsRefresh: () => (
        delayRefreshSequenceRef.current === sequence
        && Number(delayRefreshScopeRef.current.entityId) === Number(entityId)
        && Number(delayRefreshScopeRef.current.tripId) === Number(scopedTripId)
      )
    };
  }, [currentEntity?.entityId, tripId]);

  // Every pre-trip refresh publishes the same authority/display pair. The
  // marker preserves route_segments distances for ETA-disabled entities while
  // preventing a route refresh from accidentally restoring planned times.
  const publishPreTripStaticEstimate = useCallback((estimatedProjection) => {
    // This callback captures the source that issued its request. Settle only
    // while that source still owns the committed component; otherwise an older
    // response could restore clocks and Delay after source `none` took effect.
    if (committedEtaSourceRef.current !== effectiveEtaSource) {
      return;
    }
    setEtaData((previousData) => {
      const nextState = {
        ...(previousData || {}),
        ...buildEstimatedRouteState(estimatedProjection),
        estimatedEtaDisabled: effectiveEtaSource === 'none'
      };
      // The render projection already fails closed while a source-none request
      // is pending. Once that request succeeds, also remove any latent tuple or
      // derived Delay fields inherited from the prior ETA-enabled state.
      return effectiveEtaSource === 'none'
        ? stripOperationalDelayFromEstimatedState(nextState)
        : nextState;
    });
  }, [effectiveEtaSource]);

  const DEVIATION_THRESHOLD = 0.15; // 15% time deviation
  const WAYPOINT_RADIUS = 50; // 50 meters
  const MAX_TIME_WITHOUT_WAYPOINT = 5 * 60 * 1000; // 5 minutes
  const CHECK_INTERVAL = 30000; // 30 seconds
  const WAITING_ETA_HYDRATION_DELAY_MS = 2000;
  const WAITING_ETA_HYDRATION_RETRY_DELAY_MS = 3000;
  const WAITING_ETA_HYDRATION_MAX_ATTEMPTS = 3;
  const TRIP_START_ETA_SUCCESS_SUPPRESSION_MS = 10000;

  // Check if user is admin using roleUtils
  const isAdmin = checkIsAdmin(currentEntity?.userRole);
  const displayStops = useMemo(() => getTripDisplayStops(tripData), [tripData?.effective_stops, tripData?.stops]);
  const riderBookings = useMemo(() => normalizeRiderBookings(tripData), [tripData]);
  const defaultRiderBooking = useMemo(
    () => selectDefaultRiderBooking(riderBookings, tripData),
    [riderBookings, tripData]
  );
  const selectedRiderBooking = useMemo(
    () => getSelectedRiderBooking({
      bookings: riderBookings,
      selectedBookingId: selectedRiderBookingId,
      tripData
    }),
    [riderBookings, selectedRiderBookingId, tripData]
  );

  useEffect(() => {
    const defaultBookingId = defaultRiderBooking?.booking_id || null;
    if (!defaultBookingId) {
      setSelectedRiderBookingId(null);
      setHasManualRiderBookingSelection(false);
      return;
    }

    const selectedBookingStillAvailable = riderBookings.some(
      (booking) => booking.booking_id === selectedRiderBookingId
    );
    if (hasManualRiderBookingSelection && selectedBookingStillAvailable) {
      return;
    }

    if (hasManualRiderBookingSelection && !selectedBookingStillAvailable) {
      setHasManualRiderBookingSelection(false);
    }
    setSelectedRiderBookingId(defaultBookingId);
  }, [
    defaultRiderBooking?.booking_id,
    hasManualRiderBookingSelection,
    riderBookings,
    selectedRiderBookingId
  ]);

  const handleSelectRiderBooking = useCallback((bookingId) => {
    setHasManualRiderBookingSelection(true);
    setSelectedRiderBookingId(bookingId);
  }, []);

  // Prepare waypoint data for translation hook (only for user's booking waypoints)
  const bookingWaypointsData = useMemo(() => {
    if (!selectedRiderBooking || displayStops.length === 0) return null;

    const pickupStop = displayStops.find(s => s.waypoint_id === selectedRiderBooking.pickup_stop_id)
      || tripData?.stops?.find(s => s.waypoint_id === selectedRiderBooking.pickup_stop_id);
    const dropoffStop = displayStops.find(s => s.waypoint_id === selectedRiderBooking.dropoff_stop_id)
      || tripData?.stops?.find(s => s.waypoint_id === selectedRiderBooking.dropoff_stop_id);

    if (!pickupStop || !dropoffStop) return null;

    return {
      waypoints: [
        {
          waypoint_id: pickupStop.waypoint_id,
          waypoint_name: pickupStop.waypoint_name,
          name: pickupStop.waypoint_name
        },
        {
          waypoint_id: dropoffStop.waypoint_id,
          waypoint_name: dropoffStop.waypoint_name,
          name: dropoffStop.waypoint_name
        }
      ]
    };
  }, [displayStops, selectedRiderBooking, tripData?.stops]);

  // Fetch translations for user's booking waypoints (cached from ETADisplay if already fetched)
  const waypointTranslations = useWaypointTranslations(
    bookingWaypointsData,
    entitySettings,
    currentEntity
  );

  logger.debug('TripTracker render context', {
    ...getEntityLogSummary(currentEntity),
    ...getTripLogSummary(tripData)
  });

  const initialTripId = initialTripData?.id;
  const initialTripStatus = initialTripData?.status;
  const initialTripStops = initialTripData?.stops;
  const initialTripEffectiveStops = initialTripData?.effective_stops;
  const initialTripEffectiveStopsWaypointOrderVersion =
    initialTripData?.effective_stops_waypoint_order_version;
  const initialTripHasEffectiveStopsWaypointOrderVersion = Boolean(
    initialTripData
    && Object.prototype.hasOwnProperty.call(
      initialTripData,
      'effective_stops_waypoint_order_version'
    )
    && typeof initialTripEffectiveStopsWaypointOrderVersion !== 'undefined'
  );
  const initialTripUserBooking = initialTripData?.user_booking;
  const initialTripUserBookings = initialTripData?.user_bookings;

  useEffect(() => {
    // When switching to a different trip, fully reset local state
    setTripData(initialTripData);
    setEtaData(null);
    setPendingGpsSourceValue(null);
    setIsGpsSourceSwitching(false);
    gpsSourceSwitchInFlightRef.current = false;
    setGpsSourceConflict(null);
    setGpsSourceOptionsLoading(false);
    setSelectedRiderBookingId(null);
    setHasManualRiderBookingSelection(false);
    setPlanRefreshTrigger(0);
    setBoardingStatusRefreshTrigger(0);
    setLatestPlanUpdate(null);
    planUpdateSequenceRef.current = 0;
    preTripPlanRefreshInFlightRef.current = null;
    requestedPreTripPlanUpdateTokenRef.current = null;
    queuedPreTripPlanUpdateTokenRef.current = null;
    completedPreTripPlanUpdateTokenRef.current = null;
    reorderRefreshInFlightRef.current = null;
    reorderRefreshKeyRef.current = null;
    queuedReorderRefreshPayloadRef.current = null;
    completedReorderRefreshKeyRef.current = null;
    delayRefreshSequenceRef.current += 1;
    etaContinuationGenerationRef.current += 1;
    bookingRefreshSequenceRef.current += 1;
    delayRefreshPendingRef.current.clear();
    if (waitingEtaHydrationTimerRef.current) {
      clearTimeout(waitingEtaHydrationTimerRef.current);
      waitingEtaHydrationTimerRef.current = null;
    }
    waitingEtaHydrationRetryStateRef.current = {
      key: null,
      attempts: 0
    };
    tripStartEtaStateRef.current = {
      tripId: null,
      state: null,
      updatedAt: 0
    };
    setCurrentTripPlan(null);
    setLocationInfo({ speed: 0, heading: 0, lastUpdate: null, lastUpdateInvalid: false });
    const seededStates = {};
    initialTripData?.stops?.forEach((stop) => {
      if (!stop?.waypoint_id) {
        return;
      }
      if (stop.waypoint_departure_time) {
        seededStates[stop.waypoint_id] = {
          state: 'passed',
          reason: null,
          timestamp: stop.waypoint_departure_time
        };
      } else if (stop.waypoint_arrival_time && !stop.is_passed) {
        seededStates[stop.waypoint_id] = {
          state: 'dwelling',
          reason: null,
          timestamp: stop.waypoint_arrival_time
        };
      }
    });
    setWaypointStates(seededStates);
    setLastWaypointTimestamp(null);
    setNextExpectedWaypoint(null);
    setLastCheckedLocation(null);
  }, [currentEntity?.entityId, initialTripData?.id]);

  // Boarding availability is now checked via entitySettings?.boarding_tracking_enabled
  // which respects both global is_active and entity-specific setting

  // Keep local status in sync when parent updates status for the same trip
  useEffect(() => {
    if (!initialTripId) return;
    setTripData(prev => {
      if (!prev || prev.id !== initialTripId) return prev;
      if (prev.status === initialTripStatus) return prev;
      return { ...prev, status: initialTripStatus };
    });
  }, [initialTripStatus, initialTripId]);

  useEffect(() => {
    if (!initialTripId) return;
    setTripData(prev => {
      if (!prev || prev.id !== initialTripId) return prev;

      const nextData = { ...prev };
      let hasChange = false;
      const syncField = (fieldName, nextValue) => {
        if (typeof nextValue === 'undefined') {
          return;
        }
        if (prev[fieldName] !== nextValue) {
          nextData[fieldName] = nextValue;
          hasChange = true;
        }
      };

      // Parent trip refreshes are authoritative for rider stop progress and
      // booking eligibility. Sync only those display contracts so local driver
      // action state is not overwritten by unrelated prop churn.
      syncField('stops', initialTripStops);
      if (typeof initialTripEffectiveStops !== 'undefined') {
        syncField('effective_stops', initialTripEffectiveStops);
        if (initialTripHasEffectiveStopsWaypointOrderVersion) {
          syncField(
            'effective_stops_waypoint_order_version',
            initialTripEffectiveStopsWaypointOrderVersion
          );
        } else if (Object.prototype.hasOwnProperty.call(
          nextData,
          'effective_stops_waypoint_order_version'
        )) {
          // effective_stops and its proof are atomic. A successful-looking
          // same-trip refresh that omits proof must retire the previous proof,
          // or the new rows could be mislabeled as an older proven topology.
          delete nextData.effective_stops_waypoint_order_version;
          hasChange = true;
        }
      }
      syncField('user_booking', initialTripUserBooking);
      syncField('user_bookings', initialTripUserBookings);

      return hasChange ? nextData : prev;
    });
  }, [
    initialTripEffectiveStops,
    initialTripHasEffectiveStopsWaypointOrderVersion,
    initialTripEffectiveStopsWaypointOrderVersion,
    initialTripId,
    initialTripStops,
    initialTripUserBooking,
    initialTripUserBookings
  ]);

  useEffect(() => {
    if (!initialTripData?.id) return;
    setTripData(prev => {
      if (!prev || prev.id !== initialTripData.id) return prev;
      const nextData = { ...prev };
      let hasChange = false;

      if (initialTripData.selected_archived_type && prev.selected_archived_type !== initialTripData.selected_archived_type) {
        nextData.selected_archived_type = initialTripData.selected_archived_type;
        hasChange = true;
      }

      if (Array.isArray(initialTripData.available_archived_types) &&
          initialTripData.available_archived_types !== prev.available_archived_types) {
        nextData.available_archived_types = initialTripData.available_archived_types;
        hasChange = true;
      }

      return hasChange ? nextData : prev;
    });
  }, [initialTripData?.selected_archived_type, initialTripData?.available_archived_types, initialTripData?.id]);

  const memoizedTripData = useMemo(
    () => ({
      ...tripData,
      id: tripData?.id,
      status: tripData?.status,
      route_id: tripData?.route_id,
      departureTime: tripData?.departure_time,
      trip_date: tripData?.trip_date,
    }),
    [tripData] // Use entire tripData object to ensure updates when any property changes
  );
  const hasEntityTimeZoneReference = Object.prototype.hasOwnProperty.call(
    entitySettings || {},
    'time_zone_reference'
  );
  const entityTimeZoneReference = entitySettings?.time_zone_reference;
  const entityTimeZoneOwnerId = currentEntity?.entityId ?? currentEntity?.id ?? null;
  const entityTimeZoneState = useMemo(() => resolveEntityTimeZone(
    entityTimeZoneOwnerId && hasEntityTimeZoneReference
      ? { time_zone_reference: entityTimeZoneReference }
      : {},
    {
    tripDate: memoizedTripData?.trip_date,
    departureTime: memoizedTripData?.departure_time
    }
  ), [
    entityTimeZoneOwnerId,
    hasEntityTimeZoneReference,
    entityTimeZoneReference,
    memoizedTripData?.trip_date,
    memoizedTripData?.departure_time
  ]);
  const entityScheduledDeparture = useMemo(() => (
    entityTimeZoneState.status === 'ready'
      ? buildEntityScheduledDepartureDate(
          memoizedTripData?.trip_date,
          memoizedTripData?.departure_time,
          entityTimeZoneState.timeZone
        )
      : null
  ), [
    entityTimeZoneState.status,
    entityTimeZoneState.timeZone,
    memoizedTripData?.trip_date,
    memoizedTripData?.departure_time
  ]);
  // Async GPS source option responses can outlive a trip/entity transition.
  // Keep the latest render scope in a ref so old closures cannot decide
  // readiness for the currently visible trip after their request resolves.
  gpsSourceOptionsRenderScopeRef.current = {
    entityId: currentEntity?.entityId ?? null,
    tripId: tripId ?? null,
    loadAllowed: isDriver && ['scheduled', 'dispatched', 'in_progress'].includes(memoizedTripData?.status)
  };
  const isEntityMismatch = isTripEntityMismatch(memoizedTripData, currentEntity);
  const currentUserId = Number(localStorage.getItem('userId'));
  const hasOperationalDelayAccess = !isEntityMismatch
    && (
      hasRoleLevel(currentEntity?.userRole, 'driver_admin')
      || (
        currentEntity?.userRole === 'driver'
        && Number.isFinite(currentUserId)
        && Number(memoizedTripData?.driver_id) === currentUserId
      )
    );
  const operationalDelayMutable = ['scheduled', 'dispatched', 'in_progress'].includes(
    memoizedTripData?.status
  );
  const canManageOperationalDelay = hasOperationalDelayAccess
    && operationalDelayMutable
    && effectiveEtaSource !== 'none';
  useEffect(() => {
    if (!canManageOperationalDelay && showOperationalDelayDialog) {
      setShowOperationalDelayDialog(false);
    }
  }, [canManageOperationalDelay, showOperationalDelayDialog]);
  const riderScopedTripData = useMemo(() => {
    if (isDriver || !selectedRiderBooking) {
      return memoizedTripData;
    }

    // Existing map and ETA components consume the legacy singular user_booking
    // contract. Projecting only for riders keeps driver aggregate data intact
    // while making the selected booking the authoritative rider context.
    return projectSelectedRiderBookingOntoTrip(memoizedTripData, selectedRiderBooking);
  }, [isDriver, memoizedTripData, selectedRiderBooking]);
  const { userPickupStopId, userDropoffStopId } = useMemo(() => {
    if (!riderScopedTripData?.user_booking) {
      return { userPickupStopId: null, userDropoffStopId: null };
    }
    return {
      userPickupStopId: riderScopedTripData.user_booking.pickup_stop_id,
      userDropoffStopId: riderScopedTripData.user_booking.dropoff_stop_id
    };
  }, [riderScopedTripData?.user_booking]);
  const routeDisplayStops = useMemo(
    () => getTripDisplayStops(riderScopedTripData),
    [riderScopedTripData]
  );
  const gpsSourceOptionsMatchesCurrentTrip = gpsSourceOptionsScope?.tripId === tripId
    && gpsSourceOptionsScope?.entityId === currentEntity?.entityId;
  const scopedGpsSourceOptions = gpsSourceOptionsMatchesCurrentTrip ? gpsSourceOptions : null;
  const currentGpsSourceConflict = gpsSourceConflict?.entityId === currentEntity?.entityId
    && gpsSourceConflict?.tripId === tripId
    ? gpsSourceConflict
    : null;
  const hasGpsSourceOptionsSelection = Object.prototype.hasOwnProperty.call(
    scopedGpsSourceOptions || {},
    'current_selected_device_id'
  );
  const hasTripSelectedDevice = Object.prototype.hasOwnProperty.call(
    memoizedTripData || {},
    'selected_device_id'
  );
  const hasGpsSourceOptionsSharing = Object.prototype.hasOwnProperty.call(
    scopedGpsSourceOptions || {},
    'location_sharing_enabled'
  );
  const locationSharingEnabled = hasGpsSourceOptionsSharing
    ? scopedGpsSourceOptions.location_sharing_enabled === true
    : memoizedTripData?.location_sharing_enabled === true
      || memoizedTripData?.location_sharing_enabled === 1
      || memoizedTripData?.location_sharing_enabled === '1';
  const activeSelectedDeviceId = useMemo(() => {
    // Source options are fetched from the trip-scoped GPS source endpoint after
    // the incoming trip payload, so use them as the freshest source-of-truth
    // when present. The selector and browser GPS watcher must share this value
    // or stale trip data can show one source while publishing another.
    if (hasGpsSourceOptionsSelection) {
      return scopedGpsSourceOptions.current_selected_device_id;
    }
    if (hasTripSelectedDevice) {
      return memoizedTripData.selected_device_id;
    }
    return undefined;
  }, [
    scopedGpsSourceOptions?.current_selected_device_id,
    hasGpsSourceOptionsSelection,
    hasTripSelectedDevice,
    memoizedTripData?.selected_device_id
  ]);
  const currentGpsSourceValue = useMemo(() => {
    if (!locationSharingEnabled) {
      return 'off';
    }
    if (
      activeSelectedDeviceId === undefined
      || activeSelectedDeviceId === null
      || activeSelectedDeviceId === 'phone'
    ) {
      return 'phone';
    }
    if (
      scopedGpsSourceOptions?.default_device?.id
      && Number(scopedGpsSourceOptions.default_device.id) === Number(activeSelectedDeviceId)
    ) {
      return 'default';
    }
    return `device:${activeSelectedDeviceId}`;
  }, [activeSelectedDeviceId, scopedGpsSourceOptions?.default_device?.id, locationSharingEnabled]);
  const gpsDefaultDevice = scopedGpsSourceOptions?.default_device || null;
  const gpsSourceDevices = Array.isArray(scopedGpsSourceOptions?.devices) ? scopedGpsSourceOptions.devices : [];
  const otherGpsSourceDevices = gpsSourceDevices.filter(device => !device.is_default);
  const hasCurrentGpsSourceDeviceOption = typeof currentGpsSourceValue === 'string'
    && currentGpsSourceValue.startsWith('device:')
    && otherGpsSourceDevices.some(device => `device:${device.id}` === currentGpsSourceValue);
  const displayedGpsSourceValue = pendingGpsSourceValue ?? currentGpsSourceValue;
  const getDeviceIdForGpsSourceValue = useCallback((selectionValue) => {
    if (selectionValue === 'default') {
      return gpsDefaultDevice?.id ? Number(gpsDefaultDevice.id) : null;
    }
    if (typeof selectionValue === 'string' && selectionValue.startsWith('device:')) {
      const parsed = Number(selectionValue.slice('device:'.length));
      return Number.isFinite(parsed) ? parsed : null;
    }
    const parsed = Number(selectionValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [gpsDefaultDevice?.id]);
  const buildGpsSourceConflictState = useCallback((payload = {}, source = 'unknown', attemptedValue = null) => {
    if (payload?.code !== 'GPS_DEVICE_TRIP_CONFLICT' && payload?.error !== 'gps_device_trip_conflict' && !payload?.hasConflict) {
      return null;
    }

    const selectedDeviceId = payload.selected_device_id ?? payload.selectedDeviceId ?? getDeviceIdForGpsSourceValue(attemptedValue);
    return {
      entityId: currentEntity?.entityId ?? null,
      tripId,
      source,
      selectedDeviceId: selectedDeviceId === null || selectedDeviceId === undefined ? null : Number(selectedDeviceId),
      attemptedValue,
      pendingValue: attemptedValue,
      isBlocking: payload.isBlocking !== false,
      deviceName: payload.device_name || payload.deviceName || null,
      blockingTrips: Array.isArray(payload.blockingTrips) ? payload.blockingTrips : [],
      warningTrips: Array.isArray(payload.warningTrips) ? payload.warningTrips : [],
      conflictScope: payload.conflictScope || null,
      message: payload.message || null
    };
  }, [currentEntity?.entityId, getDeviceIdForGpsSourceValue, tripId]);
  const clearGpsConflictIfRecoverySelection = useCallback((selectionValue) => {
    setGpsSourceConflict((currentConflict) => {
      if (!currentConflict) {
        return currentConflict;
      }
      if (selectionValue === 'phone' || selectionValue === 'off') {
        return null;
      }
      const nextDeviceId = getDeviceIdForGpsSourceValue(selectionValue);
      if (nextDeviceId && currentConflict.selectedDeviceId && Number(nextDeviceId) !== Number(currentConflict.selectedDeviceId)) {
        return null;
      }
      return currentConflict;
    });
  }, [getDeviceIdForGpsSourceValue]);
  const updatePendingGpsSourceValue = useCallback((selectionValue) => {
    setPendingGpsSourceValue(selectionValue);
    clearGpsConflictIfRecoverySelection(selectionValue);
  }, [clearGpsConflictIfRecoverySelection]);
  useEffect(() => {
    setPendingGpsSourceValue(currentGpsSourceValue);
  }, [currentGpsSourceValue, currentEntity?.entityId, tripId]);
  const beginGpsSourceOptionsRequest = useCallback((entityId, requestTripId) => {
    const requestScope = {
      token: gpsSourceOptionsRequestRef.current.token + 1,
      entityId,
      tripId: requestTripId
    };
    gpsSourceOptionsRequestRef.current = requestScope;
    return requestScope;
  }, []);
  const isLatestGpsSourceOptionsRequest = useCallback((requestScope) => {
    const activeRequest = gpsSourceOptionsRequestRef.current;
    const activeRenderScope = gpsSourceOptionsRenderScopeRef.current;

    return activeRequest.token === requestScope.token
      && activeRequest.entityId === requestScope.entityId
      && activeRequest.tripId === requestScope.tripId
      && activeRenderScope.entityId === requestScope.entityId
      && activeRenderScope.tripId === requestScope.tripId
      && activeRenderScope.loadAllowed;
  }, []);
  const selectedGpsSourceForFeed = useMemo(() => {
    if (!locationSharingEnabled) {
      return { type: null, deviceId: null };
    }
    if (activeSelectedDeviceId === undefined) {
      return { type: null, deviceId: null };
    }
    if (activeSelectedDeviceId === null || activeSelectedDeviceId === 'phone') {
      return { type: 'phone', deviceId: null };
    }
    const deviceId = Number(activeSelectedDeviceId);
    return Number.isFinite(deviceId)
      ? { type: 'device', deviceId }
      : { type: null, deviceId: null };
  }, [activeSelectedDeviceId, locationSharingEnabled]);
  useEffect(() => {
    if (
      !isDriver
      || !['scheduled', 'dispatched', 'in_progress'].includes(memoizedTripData?.status)
      || activeSelectedDeviceId === undefined
    ) {
      return;
    }

    const shouldUsePhoneGps = locationSharingEnabled
      && (activeSelectedDeviceId === null || activeSelectedDeviceId === 'phone');

    setUsePhoneGPS((previousValue) => (
      previousValue === shouldUsePhoneGps ? previousValue : shouldUsePhoneGps
    ));
  }, [
    activeSelectedDeviceId,
    hasGpsSourceOptionsSelection,
    isDriver,
    locationSharingEnabled,
    memoizedTripData?.status
  ]);
  const loadGpsSourceOptions = useCallback(async (vehicleId = null) => {
    if (!currentEntity?.entityId || !tripId) {
      return;
    }

    const requestEntityId = currentEntity.entityId;
    const requestTripId = tripId;
    const requestScope = beginGpsSourceOptionsRequest(requestEntityId, requestTripId);

    setGpsSourceOptionsLoading(true);
    setGpsSourceOptionsError(null);
    try {
      const query = vehicleId ? `?vehicle_id=${encodeURIComponent(vehicleId)}` : '';
      const response = await axios.get(`/api/trips/${requestEntityId}/${requestTripId}/gps-source-options${query}`);
      if (!isLatestGpsSourceOptionsRequest(requestScope)) {
        return;
      }
      const optionsPayload = unwrapAxiosPayload(response) || null;
      setGpsSourceOptions(optionsPayload);
      setGpsSourceOptionsScope({ tripId: requestTripId, entityId: requestEntityId });
      const nextConflict = buildGpsSourceConflictState(optionsPayload?.gps_device_conflict, 'options');
      setGpsSourceConflict(nextConflict);
    } catch (error) {
      if (!isLatestGpsSourceOptionsRequest(requestScope)) {
        return;
      }
      logger.error('[TripTracker] Failed to load GPS source options', getSafeErrorContext(error, {
        tripId: requestTripId,
        entityId: requestEntityId,
        vehicleId
      }));
      setGpsSourceOptions(null);
      setGpsSourceOptionsScope(null);
      setGpsSourceOptionsError(error);
    } finally {
      if (isLatestGpsSourceOptionsRequest(requestScope)) {
        setGpsSourceOptionsLoading(false);
      }
    }
  }, [
    beginGpsSourceOptionsRequest,
    buildGpsSourceConflictState,
    currentEntity?.entityId,
    isLatestGpsSourceOptionsRequest,
    tripId
  ]);
  const resolveGpsSourceRequestValue = useCallback((selectionValue) => {
    if (selectionValue === 'phone' || selectionValue === 'default') {
      return selectionValue;
    }
    if (typeof selectionValue === 'string' && selectionValue.startsWith('device:')) {
      return Number(selectionValue.slice('device:'.length));
    }
    return selectionValue;
  }, []);
  useEffect(() => {
    if (!isDriver || !['scheduled', 'dispatched', 'in_progress'].includes(memoizedTripData?.status)) {
      setGpsSourceOptions(null);
      setGpsSourceOptionsScope(null);
      setGpsSourceOptionsLoading(false);
      setGpsSourceOptionsError(null);
      setShowGpsSourceChooser(false);
      setGpsSourceConflict(null);
      setUsePhoneGPS(false);
      return;
    }

    setGpsSourceOptions(null);
    setGpsSourceOptionsScope(null);
    setGpsSourceOptionsLoading(false);
    setGpsSourceOptionsError(null);
    setShowGpsSourceChooser(false);
    setGpsSourceConflict(null);
    setUsePhoneGPS(false);
    loadGpsSourceOptions(memoizedTripData?.vehicle_id || null);
  }, [
    currentEntity?.entityId,
    isDriver,
    memoizedTripData?.status,
    memoizedTripData?.vehicle_id,
    tripId
  ]);
  const showRiderTripSummary = !isDriver
    && Boolean(selectedRiderBooking)
    && !['canceled', 'cancelled'].includes(
      String(riderScopedTripData?.status || '').trim().toLowerCase()
    );


  const handleLocationUpdate = useCallback((locationData) => {
    logger.debug('[TripTracker] Location update received', {
      tripId,
      ...getLocationLogSummary(locationData)
    });
    // Only a genuine live position proves the feed is flowing. On connect the
    // server replays the last known position as event 'initial_connection', and
    // during in_progress that replay reaches this handler too; accepting it
    // would fake feed freshness from a stale position after every reconnect.
    if (locationData?.event === 'live_location') {
      const receiptMs = Date.now();
      lastLiveLocationClientMsRef.current = receiptMs;
      setHasObservedLiveLocation(true);
      setRiderFeedStale(false);
      // A recovered feed does not by itself refresh the frozen numbers; record
      // the receipt so the next ETA publication can release the snapshot.
      if (frozenLiveEtaSnapshotRef.current !== null && feedRecoveryReceiptMsRef.current === null) {
        feedRecoveryReceiptMsRef.current = receiptMs;
        setIsAwaitingLiveEtaResume(true);
      }
    }
    if (locationData) {
      const parsedLastUpdate = parseBackendUtcInstant(locationData.timestamp);
      setLocationInfo({
        speed: Math.round(locationData.speed * 3.6 || 0), // speed in km/h, convert from m/s to km/h
        heading: locationData.heading || 0,
        lastUpdate: parsedLastUpdate && !Number.isNaN(parsedLastUpdate.getTime())
          ? parsedLastUpdate
          : null,
        // Preserve the distinction between "no update received" and a backend
        // value that failed the UTC-instant contract. The latter must fail
        // closed as a trip-time placeholder, never as browser-local text.
        lastUpdateInvalid: Boolean(locationData.timestamp)
          && (!parsedLastUpdate || Number.isNaN(parsedLastUpdate.getTime())),
      });
    }
  }, [tripId]);

  const handleGpsSourceConflict = useCallback((payload = {}) => {
    if (!isDriver && !isAdmin) {
      return;
    }

    const conflictDeviceId = payload.selected_device_id ?? payload.selectedDeviceId ?? null;
    const currentDeviceId = getDeviceIdForGpsSourceValue(currentGpsSourceValue);
    const pendingDeviceId = getDeviceIdForGpsSourceValue(displayedGpsSourceValue);
    const matchesCurrentSource = conflictDeviceId === null
      || conflictDeviceId === undefined
      || Number(conflictDeviceId) === Number(currentDeviceId)
      || Number(conflictDeviceId) === Number(pendingDeviceId);

    if (!matchesCurrentSource) {
      return;
    }

    const minimalConflict = buildGpsSourceConflictState(payload, 'websocket', displayedGpsSourceValue);
    if (minimalConflict) {
      setGpsSourceConflict(minimalConflict);
      setShowGpsSourceChooser(true);
    }
    loadGpsSourceOptions(memoizedTripData?.vehicle_id || null);
  }, [
    buildGpsSourceConflictState,
    currentGpsSourceValue,
    displayedGpsSourceValue,
    getDeviceIdForGpsSourceValue,
    isAdmin,
    isDriver,
    loadGpsSourceOptions,
    memoizedTripData?.vehicle_id
  ]);

  const handleStatusUpdate = useCallback(
    (newStatus, isFromWebSocket = false, archivedType = null) => {
      logger.debug('[TripTracker] Status update received', {
        tripId,
        newStatus,
        isFromWebSocket,
        hasArchivedType: Boolean(archivedType)
      });

      // Compare against ref (not state updater mutation) for reliable timing
      const didStatusChange = tripStatusRef.current !== newStatus;
      tripStatusRef.current = newStatus; // Update ref immediately for rapid successive calls

      if (didStatusChange || archivedType) {
        setTripData((prev) => {
          const nextState = { ...prev, status: newStatus };
          if (archivedType) {
            nextState.selected_archived_type = archivedType;
          }
          return nextState;
        });
      }

      if (didStatusChange) {
        toast.info(`${t('trip_Status.trip_status_changed')} ${t(`trip_Status.status_${newStatus.toLowerCase()}`)}`);
      }

      // When status changed via WebSocket (e.g., rider reconnects and trip moved to in_progress),
      // re-fetch full trip data to get updated stops, waypoint passage data, etc.
      if (isFromWebSocket && didStatusChange && currentEntity?.entityId) {
        const owner = ++etaContinuationGenerationRef.current;
        logger.debug('[TripTracker] Status changed via WebSocket, refreshing full trip data', {
          tripId,
          entityId: currentEntity.entityId,
          newStatus
        });
        axios.get(`/api/trips/${currentEntity.entityId}/${tripId}`)
          .then(response => {
            if (owner === etaContinuationGenerationRef.current && response.data?.success && response.data?.data) {
              // Start may clear the archive without an archived_type_selected
              // event. This full row also owns an explicit SQL NULL selection.
              setTripData(response.data.data);
          }
        })
        .catch(err => {
            if (owner !== etaContinuationGenerationRef.current) return;
            logger.error('[TripTracker] Error refreshing trip data after status change', getSafeErrorContext(err, {
              tripId,
              entityId: currentEntity.entityId
            }));
        });
      }

      // Notify parent component
      if (onTripStatusChange) {
        onTripStatusChange(tripId, newStatus, isFromWebSocket, archivedType);
      }
    },
    [tripId, onTripStatusChange, currentEntity?.entityId]
  );

  // Updated handleEtaUpdate to properly manage ETA data from WebSocket
  const handleEtaUpdate = useCallback((updatedEta) => {
    if (!updatedEta || updatedEta.eta_retirement || isNoDataEtaPayload(updatedEta)) {
      // Retire every pending continuation before domain callbacks can refetch.
      // Otherwise a hydration fallback or static response can recreate an ETA
      // that the singleton has already cleared for topology or access loss.
      ++etaContinuationGenerationRef.current;
      ++delayRefreshSequenceRef.current;
      ++bookingRefreshSequenceRef.current;
      delayRefreshPendingRef.current.clear();
      if (waitingEtaHydrationTimerRef.current) clearTimeout(waitingEtaHydrationTimerRef.current);
      waitingEtaHydrationTimerRef.current = null;
      waitingEtaHydrationRetryStateRef.current = { key: null, attempts: 0 };
      tripStartEtaStateRef.current = { tripId: null, state: null, updatedAt: 0 };
      setEtaData(null);
    }
    if (!updatedEta) {
      setEtaData(null);
      frozenLiveEtaSnapshotRef.current = null;
      feedRecoveryReceiptMsRef.current = null;
      setFrozenLiveEtaSnapshot(null);
      setIsAwaitingLiveEtaResume(false);
      return;
    }
    if (updatedEta.eta_retirement || isNoDataEtaPayload(updatedEta)) {
      frozenLiveEtaSnapshotRef.current = null;
      feedRecoveryReceiptMsRef.current = null;
      setFrozenLiveEtaSnapshot(null);
      setIsAwaitingLiveEtaResume(false);
    }

    // Frozen-presentation recovery: the first ETA accepted after the recovery
    // live_location receipt resumes live display. Causal ordering is the whole
    // test — this handler can only run for a payload accepted after the ref was
    // set, so no wall-clock comparison is needed (a same-millisecond callback
    // would otherwise strand the snapshot). It runs before every early return,
    // including the no-data retirement, so "Updating ETA…" can never stick.
    // This is a publication-order heuristic, not provenance: it does not prove
    // the resumed ETA consumed the recovery position, but it is preferred over
    // a card that never resumes after a transient feed blip.
    if (feedRecoveryReceiptMsRef.current !== null) {
      feedRecoveryReceiptMsRef.current = null;
      setIsAwaitingLiveEtaResume(false);
      setFrozenLiveEtaSnapshot(null);
    }

    if (isNoDataEtaPayload(updatedEta)) {
      // Keep the retirement watermark in WebSocketService/the hook ref, but do
      // not expose it as displayable live ETA state.
      setEtaData((previousData) => {
        if (!previousData?.websocket && !previousData?.live) return previousData;
        const nextData = { ...previousData };
        delete nextData.websocket;
        // `live` is normally derived from `websocket` at render time. Remove a
        // stored copy too so no alternate writer can keep stale live rows
        // selected after the authoritative retirement watermark is accepted.
        delete nextData.live;
        return Object.keys(nextData).length > 0 ? nextData : null;
      });
      return;
    }

    if (shouldRejectEtaForNoneSource(effectiveEtaSource, updatedEta)) {
      logger.debug('[TripTracker] Ignoring provider ETA because entity ETA source is none', {
        tripId,
        ...getEtaLogSummary(updatedEta)
      });
      return;
    }

    logger.debug('[TripTracker] ETA update received from WebSocket', {
      tripId,
      ...getEtaLogSummary(updatedEta)
    });
    
    // Update local state with WebSocket data
    setEtaData(prevData => ({
      ...prevData,
      websocket: createCanonicalLiveEtaPayload(updatedEta, prevData?.websocket || null)
    }));
    
    // Note: Don't call sendEtaUpdate here to prevent infinite loop
    // The data is already coming from WebSocket, so it's already been broadcast
  }, [effectiveEtaSource, tripId]);

  useEffect(() => {
    if (!shouldRejectEtaForNoneSource(effectiveEtaSource, etaData?.websocket)) {
      return;
    }

    logger.debug('[TripTracker] Clearing stale provider ETA because entity ETA source is none', {
      tripId,
      ...getEtaLogSummary(etaData.websocket)
    });

    setEtaData(prevData => {
      if (!shouldRejectEtaForNoneSource(effectiveEtaSource, prevData?.websocket)) {
        return prevData;
      }

      const remainingEtaData = { ...(prevData || {}) };
      delete remainingEtaData.websocket;
      return Object.keys(remainingEtaData).length > 0 ? remainingEtaData : null;
    });
  }, [effectiveEtaSource, etaData?.websocket, tripId]);

  useEffect(() => {
    // The rider staleness check rides this tick, and a feed can die while no
    // live ETA payload is held at all (ETA never arrived, or was retired). Keep
    // ticking for a rider who has observed at least one live position, so
    // staleness is still detected without a current websocket ETA.
    const hasLiveEtaAnchor = Boolean(etaData?.websocket?.client_received_at_ms);
    const needsRiderStalenessTick = !isDriver && hasObservedLiveLocation;
    if (memoizedTripData?.status !== 'in_progress' || (!hasLiveEtaAnchor && !needsRiderStalenessTick)) {
      return;
    }

    setLiveEtaNowTick(Date.now());
    const intervalId = setInterval(() => {
      setLiveEtaNowTick(Date.now());
    }, 10000);

    return () => clearInterval(intervalId);
  }, [
    etaData?.websocket?.client_received_at_ms,
    hasObservedLiveLocation,
    isDriver,
    memoizedTripData?.status
  ]);

  const liveEtaData = useMemo(() => {
    if (memoizedTripData?.status !== 'in_progress') {
      return null;
    }

    return deriveLiveEtaPayload(etaData?.websocket, liveEtaNowTick);
  }, [etaData?.websocket, liveEtaNowTick, memoizedTripData?.status]);

  const displayEtaData = useMemo(() => {
    const isPreTripDistanceOnlyPresentation = effectiveEtaSource === 'none'
      && ['scheduled', 'dispatched'].includes(memoizedTripData?.status);
    if (!etaData) {
      // Preference authority also applies before the first estimate arrives.
      // This render-only marker suppresses fallback clocks without satisfying
      // the request-state hydration guard or creating a synthetic estimate.
      if (isPreTripDistanceOnlyPresentation) {
        return { estimatedEtaDisabled: true };
      }
      // A no-data retirement can clear the request state entirely. While frozen,
      // the last-known rows are still the honest thing to show, so the snapshot
      // outlives the retired state instead of blanking the card.
      return frozenLiveEtaSnapshot !== null
        ? { live: frozenLiveEtaSnapshot.payload, isStalePresentation: true }
        : null;
    }

    // While frozen, the snapshot replaces the ticking derived payload so later
    // etaData.websocket mutations and presentation ticks cannot move the
    // displayed numbers.
    const isFrozenPresentation = frozenLiveEtaSnapshot !== null;
    const displayState = {
      ...etaData,
      live: isFrozenPresentation ? frozenLiveEtaSnapshot.payload : liveEtaData,
      ...(isFrozenPresentation ? { isStalePresentation: true } : {})
    };
    if (!isPreTripDistanceOnlyPresentation) {
      return displayState;
    }

    // Keep request state untouched: its old disabled marker is what causes the
    // replacement hydration to run. The render-only copy fails closed while B
    // is pending or rejected, strips stale Delay authority, and selects the
    // existing distance-only sanitizer without blanking reusable route rows.
    const distanceOnlyState = stripOperationalDelayFromEstimatedState(displayState);
    return {
      ...distanceOnlyState,
      estimatedEtaDisabled: true
    };
  }, [etaData, effectiveEtaSource, frozenLiveEtaSnapshot, liveEtaData, memoizedTripData?.status]);

  const refreshTripAfterManualSkip = useCallback(() => {
    if (!currentEntity?.entityId || !tripId) {
      return Promise.resolve();
    }

    // Deduplicate overlapping refresh attempts from local action + WebSocket event.
    if (manualSkipRefreshInFlightRef.current) {
      return manualSkipRefreshInFlightRef.current;
    }

    setPlanRefreshTrigger(prev => prev + 1);

    const refreshPromise = axios.get(`/api/trips/${currentEntity.entityId}/${tripId}`)
      .then(response => {
        const tripPayload = unwrapAxiosPayload(response);
        if (tripPayload) {
          setTripData(tripPayload);
        }
      })
      .catch(err => {
        logger.error('[TripTracker] Error refreshing trip data after manual skip', getSafeErrorContext(err, {
          tripId,
          entityId: currentEntity.entityId
        }));
      })
      .finally(() => {
        manualSkipRefreshInFlightRef.current = null;
      });

    manualSkipRefreshInFlightRef.current = refreshPromise;
    return refreshPromise;
  }, [currentEntity?.entityId, tripId]);

  const handleWaypointStateChange = useCallback((changePayload) => {
    if (!changePayload?.waypointId) {
      return;
    }

    logger.debug('[TripTracker] Waypoint state change received', {
      tripId,
      ...getWaypointEventLogSummary(changePayload)
    });

    setWaypointStates(prev => ({
      ...prev,
      [changePayload.waypointId]: {
        state: changePayload.state,
        reason: changePayload.reason || null,
        timestamp: changePayload.timestamp || new Date().toISOString()
      }
    }));

    setTripData(prev => {
      if (!prev?.stops) {
        return prev;
      }

      const updatedStops = prev.stops.map(stop => {
        if (stop.waypoint_id !== changePayload.waypointId) {
          return stop;
        }

        if (changePayload.state === 'dwelling') {
          return {
            ...stop,
            is_passed: false,
            is_skipped: false,
            is_manual_skip: false,
            waypoint_arrival_time: changePayload.timestamp || new Date().toISOString()
          };
        }

        if (changePayload.state === 'passed' && changePayload.reason === 'manual_skip') {
          return {
            ...stop,
            is_passed: true,
            is_skipped: true,
            is_manual_skip: true,
            waypoint_arrival_time: null,
            waypoint_departure_time: null
          };
        }

        if (changePayload.state === 'passed') {
          const timestamp = changePayload.timestamp || new Date().toISOString();
          return {
            ...stop,
            is_passed: true,
            is_manual_skip: false,
            waypoint_departure_time: timestamp,
            waypoint_arrival_time: stop.waypoint_arrival_time || timestamp
          };
        }

        // Handle 'pending' state from manual correction - clear passage data
        if (changePayload.state === 'pending') {
          return {
            ...stop,
            is_passed: false,
            is_skipped: false,
            is_manual_skip: false,
            waypoint_arrival_time: null,
            waypoint_departure_time: null
          };
        }

        return stop;
      });

      return { ...prev, stops: updatedStops };
    });

    if (tripStatusRef.current === 'in_progress') {
      const { ownsRefresh } = beginOperationalDelayStaticRefresh();
      Promise.resolve(fetchEstimatedRouteDurationRef.current?.({ publishState: false }))
        .then((estimatedProjection) => {
          if (!ownsRefresh() || !estimatedProjection) return;
          setEtaData((previousData) => ({
            ...(previousData || {}),
            ...buildEstimatedRouteState(estimatedProjection)
          }));
        })
        .catch((error) => {
          if (!ownsRefresh()) return;
          logger.error('[TripTracker] Failed to refresh static Delay after waypoint state change', getSafeErrorContext(error, {
            tripId,
            entityId: currentEntity?.entityId,
            waypointId: changePayload.waypointId
          }));
        });
    }

    if (
      changePayload.reason === 'manual_skip'
      || changePayload.reason === 'pretrip_manual_skip_correction'
    ) {
      refreshTripAfterManualSkip();
    }
  }, [beginOperationalDelayStaticRefresh, currentEntity?.entityId, refreshTripAfterManualSkip, tripId]);

  const handleArchivedTypeChange = useCallback((archivedType) => {
    if (!archivedType) {
      return;
    }
    setTripData(prev => {
      if (!prev) {
        return prev;
      }
      if (prev.selected_archived_type === archivedType) {
        return prev;
      }
      return {
        ...prev,
        selected_archived_type: archivedType
      };
    });
  }, []);

  const refreshAfterWaypointReorder = useCallback((payload = {}, options = {}) => {
    if (!currentEntity?.entityId || !tripId || isEntityMismatch) {
      return Promise.resolve();
    }

    const refreshKey = buildWaypointReorderRefreshKey(payload);
    if (
      refreshKey
      && completedReorderRefreshKeyRef.current === refreshKey
      && !reorderRefreshInFlightRef.current
    ) {
      return Promise.resolve();
    }

    if (reorderRefreshInFlightRef.current) {
      if (!refreshKey || refreshKey === reorderRefreshKeyRef.current) {
        return reorderRefreshInFlightRef.current;
      }

      queuedReorderRefreshPayloadRef.current = {
        payload,
        options
      };
      return reorderRefreshInFlightRef.current;
    }

    const runSingleRefresh = async (refreshPayload, refreshOptions) => {
      const currentRefreshKey = buildWaypointReorderRefreshKey(refreshPayload);
      reorderRefreshKeyRef.current = currentRefreshKey;
      const staticRefreshOwner = beginOperationalDelayStaticRefresh();
      setIsRefreshing(true);
      setCurrentTripPlan(null);
      setPlanRefreshTrigger(prev => prev + 1);

      if (refreshPayload?.sync?.status === 'incomplete') {
        logger.warn('[TripTracker] Waypoint order sync incomplete before local refresh', {
          tripId,
          entityId: currentEntity.entityId,
          source: refreshOptions?.source,
          syncStatus: refreshPayload.sync.status
        });
      }

      const tripResponse = await axios.get(`/api/trips/${currentEntity.entityId}/${tripId}`);
      const tripPayload = unwrapAxiosPayload(tripResponse);
      if (tripPayload) {
        setTripData(tripPayload);
      }

      const statusForEstimate = tripPayload?.status || tripStatusRef.current;
      if (['scheduled', 'dispatched', 'in_progress'].includes(statusForEstimate)) {
        const estimatedProjection = await fetchEstimatedRouteDurationRef.current?.({ publishState: false });
        if (estimatedProjection && staticRefreshOwner.ownsRefresh()) {
          setEtaData(prev => ({
            ...(prev || {}),
            ...buildEstimatedRouteState(estimatedProjection)
          }));
        }
      }

      if (currentRefreshKey) {
        completedReorderRefreshKeyRef.current = currentRefreshKey;
      }
    };

    const runRefreshLoop = async () => {
      let nextPayload = payload;
      let nextOptions = options;

      while (nextPayload) {
        await runSingleRefresh(nextPayload, nextOptions);
        const queued = queuedReorderRefreshPayloadRef.current;
        queuedReorderRefreshPayloadRef.current = null;
        nextPayload = queued?.payload || null;
        nextOptions = queued?.options || {};
      }
    };

    const refreshPromise = runRefreshLoop()
      .catch((error) => {
        logger.error('[TripTracker] Error refreshing trip data after waypoint order change', getSafeErrorContext(error, {
          tripId,
          entityId: currentEntity?.entityId
        }));
        throw error;
      })
      .finally(() => {
        reorderRefreshInFlightRef.current = null;
        reorderRefreshKeyRef.current = null;
        setIsRefreshing(false);
      });

    reorderRefreshInFlightRef.current = refreshPromise;
    return refreshPromise;
  }, [beginOperationalDelayStaticRefresh, currentEntity?.entityId, tripId, isEntityMismatch]);

  // Handler for waypoint order changes (reorderable trips)
  const handleWaypointOrderChanged = useCallback((payload) => {
    logger.debug('[TripTracker] Waypoint order changed', {
      tripId,
      ...getWaypointEventLogSummary(payload)
    });

    refreshAfterWaypointReorder(payload, { source: 'websocket' })
      .then(() => {
        toast.info(t('waypointOrderUpdated'));
      })
      .catch(() => {});
  }, [refreshAfterWaypointReorder, t, tripId]);

  // Handler for booking changes — refresh trip data to update is_skipped/cumulative
  const handleTripBookingsChanged = useCallback((payload) => {
    logger.debug('[TripTracker] Trip bookings changed', {
      tripId,
      entityId: currentEntity?.entityId,
      eventType: payload?.eventType || payload?.type || null,
      hasPayload: Boolean(payload)
    });
    const isDelayRefresh = payload?.eventType === 'trip_delay_updated';
    if (isDelayRefresh && effectiveEtaSource === 'none') {
      return Promise.resolve(null);
    }

    if (!isDelayRefresh) {
      const sequence = ++bookingRefreshSequenceRef.current;
      const ownsRefresh = () => sequence === bookingRefreshSequenceRef.current;
      setPlanRefreshTrigger(prev => prev + 1);
      setBoardingStatusRefreshTrigger(prev => prev + 1);
      axios.get(`/api/trips/${currentEntity?.entityId}/${tripId}`)
        .then(response => {
          if (!ownsRefresh()) return;
          const tripPayload = unwrapAxiosPayload(response);
          if (tripPayload) {
            setTripData(tripPayload);
          }
        })
        .catch(err => {
          if (!ownsRefresh()) return;
          logger.error('[TripTracker] Error refreshing trip data after booking change', getSafeErrorContext(err, {
            tripId,
            entityId: currentEntity?.entityId
          }));
        });
      return undefined;
    }

    const entityId = currentEntity?.entityId;
    const scopedTripId = tripId;
    const changeKey = payload?.changeKey || payload?.change?.changeKey || null;
    const pendingKey = changeKey ? `${entityId}:${scopedTripId}:${changeKey}` : null;
    if (pendingKey && delayRefreshPendingRef.current.has(pendingKey)) {
      return delayRefreshPendingRef.current.get(pendingKey);
    }

    setPlanRefreshTrigger(prev => prev + 1);
    setBoardingStatusRefreshTrigger(prev => prev + 1);
    const { ownsRefresh } = beginOperationalDelayStaticRefresh();
    const fetchRefreshPair = () => Promise.all([
      axios.get(`/api/trips/${entityId}/${scopedTripId}`),
      fetchEstimatedRouteDurationRef.current?.({ publishState: false })
    ]);

    let refreshPromise;
    refreshPromise = (async () => {
      try {
        let pair;
        try {
          pair = await fetchRefreshPair();
        } catch (error) {
          if (!isOperationalDelayProjectionStale(error)) throw error;
          pair = await fetchRefreshPair();
        }

        if (!ownsRefresh()) return null;
        const tripPayload = unwrapAxiosPayload(pair[0]);
        if (tripPayload) setTripData(tripPayload);
        setEtaData((previousData) => ({
          ...(previousData || {}),
          ...buildEstimatedRouteState(pair[1] ?? null)
        }));
        return {
          trip: tripPayload,
          estimated: normalizeEstimatedRouteProjection(pair[1] ?? null).estimatedData
        };
      } catch (error) {
        // Superseded or out-of-scope work has no state or error ownership. The
        // latest dialog-owned failure reaches its existing loadFailed toast;
        // the latest WebSocket-owned failure is logged by the hook caller.
        if (!ownsRefresh()) return null;
        throw error;
      } finally {
        if (pendingKey && delayRefreshPendingRef.current.get(pendingKey) === refreshPromise) {
          delayRefreshPendingRef.current.delete(pendingKey);
        }
      }
    })();

    if (pendingKey) delayRefreshPendingRef.current.set(pendingKey, refreshPromise);
    return refreshPromise;
  }, [beginOperationalDelayStaticRefresh, currentEntity?.entityId, effectiveEtaSource, tripId]);

  const queueTripPlanUpdate = useCallback((plan, source = 'websocket') => {
    logger.debug('[TripTracker] Trip plan updated', {
      tripId,
      ...getPlanLogSummary(plan, source)
    });
    planUpdateSequenceRef.current += 1;
    setLatestPlanUpdate({
      tripId,
      plan,
      updatedAt: planUpdateSequenceRef.current,
      source
    });
  }, [tripId]);

  // Handler for websocket plan updates — trigger map plan refresh so polylines/totals stay in sync
  const handleTripPlanUpdated = useCallback((plan) => {
    queueTripPlanUpdate(plan, 'websocket');
  }, [queueTripPlanUpdate]);

  const handleLocalTripPlanUpdated = useCallback((plan) => {
    queueTripPlanUpdate(plan, 'local_variant_selection');
  }, [queueTripPlanUpdate]);

  const handlePlanStateChange = useCallback((plan) => {
    setCurrentTripPlan(plan);
  }, []);

  // Handler for waypoint deviation alerts (admin only)
  const handleWaypointDeviationAlert = useCallback((payload) => {
    logger.debug('[TripTracker] Waypoint deviation alert', {
      tripId,
      expectedWaypointId: payload?.expectedWaypointId ?? null,
      arrivedWaypointId: payload?.arrivedWaypointId ?? payload?.arrivedAt ?? null
    });

    if (isAdmin) {
      // Show toast notification for admin
      toast.warn(
        t('waypointDeviationAlert', {
          expected: payload.expectedWaypointName || payload.expectedWaypointId,
          actual: payload.arrivedWaypointName || payload.arrivedAt
        }),
        { autoClose: 10000 } // Keep visible longer
      );
    }
  }, [isAdmin, t, tripId]);

  // Resolve actor eligibility independently from lifecycle/action eligibility.
  // The backend repeats this authorization from locked database state.
  const canActOnManualWaypoints = useMemo(() => {
    const currentUserId = parseInt(localStorage.getItem('userId'));

    logger.debug('[TripTracker] manual waypoint actor check', {
      entityId: currentEntity?.entityId,
      tripId: tripData?.id,
      tripStatus: memoizedTripData?.status,
      isAdmin,
      isDriver,
      currentUserId,
      tripDriverId: tripData?.driver_id,
      driverMatch: tripData?.driver_id === currentUserId
    });

    if (!currentEntity?.entityId || !tripData?.id) return false;
    if (isAdmin) return true;
    if (isDriver && tripData?.driver_id === currentUserId) return true;

    return false;
  }, [
    isAdmin,
    isDriver,
    currentEntity?.entityId,
    tripData?.id,
    tripData?.driver_id,
    memoizedTripData?.status
  ]);

  // Active arrival/departure/correction semantics stay in-progress only.
  const canManualOverride = canActOnManualWaypoints
    && memoizedTripData?.status === 'in_progress';
  // Skip is communication/ETA input before departure and remains available
  // through the existing active-trip behavior.
  const canManualSkip = canActOnManualWaypoints
    && ['scheduled', 'dispatched', 'in_progress'].includes(memoizedTripData?.status);
  const canCorrectPreTripManualSkip = canActOnManualWaypoints
    && ['scheduled', 'dispatched'].includes(memoizedTripData?.status);

  // Handler for manual waypoint arrival
  const handleManualArrival = useCallback(async (waypointId, waypointName) => {
    if (!canManualOverride) {
      toast.error(t('notAuthorizedForManualOverride'));
      return;
    }

    try {
      logger.debug('[TripTracker] Manual arrival requested', {
        tripId: tripData.id,
        entityId: currentEntity.entityId,
        waypointId
      });
      const result = await manualWaypointService.markArrived(
        currentEntity.entityId,
        tripData.id,
        waypointId
      );

      if (result.alreadyArrived) {
        // Idempotent response - waypoint was already marked as arrived
        toast.info(t('waypointAlreadyArrived', { waypointName }));
      } else {
        toast.success(t('waypointMarkedArrived', { waypointName }));

        // Update local waypoint state
        setWaypointStates(prev => ({
          ...prev,
          [waypointId]: {
            state: 'dwelling',
            reason: 'manual_arrival',
            timestamp: new Date().toISOString()
          }
        }));
      }
    } catch (error) {
      logger.error('[TripTracker] Manual arrival error', getSafeErrorContext(error, {
        tripId: tripData?.id,
        entityId: currentEntity?.entityId,
        waypointId
      }));
      const errorMessage = error.response?.data?.error || t('failedToMarkArrival');
      toast.error(errorMessage);
    }
  }, [canManualOverride, currentEntity?.entityId, tripData?.id, t]);

  // Handler for manual waypoint departure
  const handleManualDeparture = useCallback(async (waypointId, waypointName) => {
    if (!canManualOverride) {
      toast.error(t('notAuthorizedForManualOverride'));
      return;
    }

    try {
      logger.debug('[TripTracker] Manual departure requested', {
        tripId: tripData.id,
        entityId: currentEntity.entityId,
        waypointId
      });
      const result = await manualWaypointService.markDeparted(
        currentEntity.entityId,
        tripData.id,
        waypointId
      );

      if (result.alreadyDeparted) {
        // Idempotent response - waypoint was already marked as departed
        toast.info(t('waypointAlreadyDeparted', { waypointName }));
      } else {
        toast.success(t('waypointMarkedDeparted', { waypointName }));

        // Update local waypoint state
        setWaypointStates(prev => ({
          ...prev,
          [waypointId]: {
            state: 'passed',
            reason: 'manual_departure',
            timestamp: new Date().toISOString()
          }
        }));
      }
    } catch (error) {
      logger.error('[TripTracker] Manual departure error', getSafeErrorContext(error, {
        tripId: tripData?.id,
        entityId: currentEntity?.entityId,
        waypointId
      }));
      const errorMessage = error.response?.data?.error || t('failedToMarkDeparture');
      toast.error(errorMessage);
    }
  }, [canManualOverride, currentEntity?.entityId, tripData?.id, t]);

  // Handler for manual waypoint correction (reset to pending)
  const handleManualCorrection = useCallback(async (waypointId, waypointName) => {
    if (!canManualOverride) {
      toast.error(t('notAuthorizedForManualOverride'));
      return;
    }

    try {
      logger.debug('[TripTracker] Manual correction requested', {
        tripId: tripData.id,
        entityId: currentEntity.entityId,
        waypointId
      });
      const optionsResult = await manualWaypointService.getCorrectionOptions(
        currentEntity.entityId,
        tripData.id,
        waypointId
      );
      const coLocatedWaypoints = optionsResult?.data?.coLocatedWaypoints || [];

      // Always use a single correction dialog flow.
      const selectedWaypointId = await new Promise((resolve) => {
        correctionSelectionResolverRef.current = resolve;
        setCorrectionDialogState({
          isOpen: true,
          waypointId,
          waypointName,
          coLocatedWaypoints
        });
      });

      if (selectedWaypointId === undefined) {
        // Dialog cancelled, no mutation requested.
        return;
      }

      setIsCorrectionDialogLoading(true);
      const result = await manualWaypointService.selectAfterCorrect(
        currentEntity.entityId,
        tripData.id,
        waypointId,
        selectedWaypointId,
        'manual_correction_by_driver'
      );
      setIsCorrectionDialogLoading(false);

      if (result.data?.alreadyPending) {
        // Waypoint was already in pending state
        toast.info(t('waypointAlreadyPending', { waypointName }));
      } else {
        toast.success(t('waypointCorrected', { waypointName }));

        // Update local waypoint state to pending
        setWaypointStates(prev => {
          const next = {
            ...prev,
            [waypointId]: {
              state: 'pending',
              reason: 'manual_correction',
              timestamp: new Date().toISOString()
            }
          };
          if (Number.isFinite(selectedWaypointId)) {
            next[selectedWaypointId] = {
              state: 'dwelling',
              reason: 'manual_correction_selected_waypoint',
              timestamp: new Date().toISOString()
            };
          }
          return next;
        });

        // Also clear the is_passed and departure time from local tripData
        setTripData(prev => {
          if (!prev?.stops) return prev;

          const updatedStops = prev.stops.map(stop => {
            if (stop.waypoint_id === waypointId) {
              return {
                ...stop,
                is_passed: false,
                waypoint_arrival_time: null,
                waypoint_departure_time: null
              };
            }
            if (Number.isFinite(selectedWaypointId) && stop.waypoint_id === selectedWaypointId) {
              return {
                ...stop,
                is_passed: false,
                waypoint_arrival_time: new Date().toISOString(),
                waypoint_departure_time: null
              };
            }
            return stop;
          });

          return { ...prev, stops: updatedStops };
        });
      }
    } catch (error) {
      setIsCorrectionDialogLoading(false);
      logger.error('[TripTracker] Manual correction error', getSafeErrorContext(error, {
        tripId: tripData?.id,
        entityId: currentEntity?.entityId,
        waypointId
      }));
      const errorMessage = error.response?.data?.error || t('failedToCorrectWaypoint');
      toast.error(errorMessage);
    }
  }, [canManualOverride, currentEntity?.entityId, tripData?.id, t]);

  const handleCorrectionDialogClose = useCallback(() => {
    setCorrectionDialogState({
      isOpen: false,
      waypointId: null,
      waypointName: '',
      coLocatedWaypoints: []
    });
    if (correctionSelectionResolverRef.current) {
      correctionSelectionResolverRef.current(undefined);
      correctionSelectionResolverRef.current = null;
    }
  }, []);

  const handleCorrectionDialogConfirm = useCallback((selectedWaypointId) => {
    if (correctionSelectionResolverRef.current) {
      correctionSelectionResolverRef.current(selectedWaypointId);
      correctionSelectionResolverRef.current = null;
    }
    setCorrectionDialogState({
      isOpen: false,
      waypointId: null,
      waypointName: '',
      coLocatedWaypoints: []
    });
  }, []);

  const handleManualSkip = useCallback(async (waypointId, waypointName) => {
    if (!canManualSkip) {
      toast.error(t('notAuthorizedForManualOverride'));
      return;
    }

    try {
      const result = await manualWaypointService.skipWaypoint(
        currentEntity.entityId,
        tripData.id,
        waypointId
      );

      toast.success(t('waypointSkipped', { waypointName }));

      setWaypointStates(prev => ({
        ...prev,
        [waypointId]: {
          state: 'passed',
          reason: 'manual_skip',
          timestamp: result?.data?.timestamp || new Date().toISOString()
        }
      }));

      setTripData(prev => {
        if (!prev?.stops) return prev;

        const updatedStops = prev.stops.map(stop => {
          if (stop.waypoint_id !== waypointId) return stop;
          return {
            ...stop,
            is_passed: true,
            is_skipped: true,
            is_manual_skip: true,
            waypoint_arrival_time: null,
            waypoint_departure_time: null
          };
        });

        return { ...prev, stops: updatedStops };
      });

      refreshTripAfterManualSkip();
    } catch (error) {
      logger.error('[TripTracker] Manual skip error', getSafeErrorContext(error, {
        tripId: tripData?.id,
        entityId: currentEntity?.entityId,
        waypointId
      }));
      const errorMessage = error.response?.data?.error || t('failedToSkipWaypoint');
      toast.error(errorMessage);
    }
  }, [canManualSkip, currentEntity?.entityId, tripData?.id, t, refreshTripAfterManualSkip]);

  const handlePreTripManualCorrection = useCallback(async (waypointId, waypointName) => {
    if (!canCorrectPreTripManualSkip) {
      toast.error(t('notAuthorizedForManualOverride'));
      return;
    }

    try {
      await manualWaypointService.correctWaypoint(
        currentEntity.entityId,
        tripData.id,
        waypointId,
        'pretrip_manual_skip_correction'
      );

      toast.success(t('waypointCorrected', { waypointName }));
      setWaypointStates(prev => ({
        ...prev,
        [waypointId]: {
          state: 'pending',
          reason: 'pretrip_manual_skip_correction',
          timestamp: new Date().toISOString()
        }
      }));
      setTripData(prev => {
        if (!prev?.stops) return prev;

        return {
          ...prev,
          stops: prev.stops.map(stop => (
            stop.waypoint_id === waypointId
              ? {
                  ...stop,
                  is_passed: false,
                  is_skipped: false,
                  is_manual_skip: false,
                  waypoint_arrival_time: null,
                  waypoint_departure_time: null
                }
              : stop
          ))
        };
      });

      await refreshTripAfterManualSkip();
    } catch (error) {
      logger.error('[TripTracker] Pre-trip manual correction error', getSafeErrorContext(error, {
        tripId: tripData?.id,
        entityId: currentEntity?.entityId,
        waypointId
      }));
      const errorMessage = error.response?.data?.error || t('failedToCorrectWaypoint');
      toast.error(errorMessage);
    }
  }, [
    canCorrectPreTripManualSkip,
    currentEntity?.entityId,
    tripData?.id,
    t,
    refreshTripAfterManualSkip
  ]);

  // Call useTripTracking hook BEFORE any functions that use its values
  const {
    gpsActive,
    selectedGpsFeedActive = false,
    gpsError,
    currentLocation,
    gpsTrail, // GPS breadcrumbs for map display (accumulated from local GPS or WebSocket)
    connectionStatus,
    sendEtaUpdate,
    sendStatusUpdate,
    getWebSocketEta,
    acceptEtaUpdate: acceptEtaUpdateFromHook,
    fetchActualTripDuration,
    fetchEstimatedRouteDuration,
  } = useTripTracking(
    tripId,
    memoizedTripData?.status,
    isDriver && usePhoneGPS && selectedGpsSourceForFeed.type === 'phone',
    handleLocationUpdate,
    handleEtaUpdate,
    handleStatusUpdate,
    handleWaypointStateChange,
    handleArchivedTypeChange,
    currentEntity,
    memoizedTripData,
    {
      onWaypointOrderChanged: handleWaypointOrderChanged,
      onWaypointDeviationAlert: handleWaypointDeviationAlert,
      onGpsTrailHistory: null,
      onTripBookingsChanged: handleTripBookingsChanged,
      onTripPlanUpdated: handleTripPlanUpdated,
      onGpsSourceConflict: handleGpsSourceConflict,
      selectedGpsSource: selectedGpsSourceForFeed
    }
  );

  // Older focused component mocks expose only the state sink. Runtime ETA
  // ingress always uses the hook's shared freshness gate.
  const acceptEtaUpdate = useCallback((etaPayload, source) => {
    if (typeof acceptEtaUpdateFromHook === 'function') {
      return acceptEtaUpdateFromHook(etaPayload, source);
    }
    handleEtaUpdate(etaPayload);
    return true;
  }, [acceptEtaUpdateFromHook, handleEtaUpdate]);

  useEffect(() => {
    fetchEstimatedRouteDurationRef.current = fetchEstimatedRouteDuration;
  }, [fetchEstimatedRouteDuration]);

  // Mirror the frozen snapshot into a ref so the location handler can test it
  // without re-creating its callback on every freeze/release.
  useEffect(() => {
    frozenLiveEtaSnapshotRef.current = frozenLiveEtaSnapshot;
  }, [frozenLiveEtaSnapshot]);

  const isTerminalTripStatus = memoizedTripData?.status === 'completed'
    || memoizedTripData?.status === 'canceled'
    || memoizedTripData?.status === 'cancelled';

  // Frozen-presentation state is scoped to one active trip. Reset it whenever
  // the tracked trip/entity changes or the trip reaches a terminal status, so a
  // snapshot can never leak into another trip or a finished one.
  useEffect(() => {
    lastLiveLocationClientMsRef.current = null;
    feedRecoveryReceiptMsRef.current = null;
    setHasObservedLiveLocation(false);
    setRiderFeedStale(false);
    setFrozenLiveEtaSnapshot(null);
    setIsAwaitingLiveEtaResume(false);
  }, [currentEntity?.entityId, isTerminalTripStatus, tripId]);

  // Rider-only feed staleness, evaluated on the existing 10s presentation tick.
  // While the socket is unhealthy the browser cannot observe the feed at all,
  // so staleness is forced false and the connection notice speaks instead.
  useEffect(() => {
    if (isDriver
      || memoizedTripData?.status !== 'in_progress'
      || connectionStatus !== 'connected'
      || lastLiveLocationClientMsRef.current === null) {
      setRiderFeedStale(false);
      return;
    }

    setRiderFeedStale(Date.now() - lastLiveLocationClientMsRef.current > GPS_FEED_FRESHNESS_MS);
  }, [connectionStatus, isDriver, liveEtaNowTick, memoizedTripData?.status]);

  // One-shot freeze. The snapshot is anchored at the deterministic staleness
  // boundary (last receipt + freshness window), never the wake-up wall clock,
  // so a backgrounded tab cannot age the numbers before capturing them.
  useEffect(() => {
    if (!riderFeedStale) {
      return;
    }

    setFrozenLiveEtaSnapshot((previousSnapshot) => {
      if (previousSnapshot !== null) {
        return previousSnapshot;
      }
      const anchorMs = (lastLiveLocationClientMsRef.current ?? Date.now()) + GPS_FEED_FRESHNESS_MS;
      return { payload: deriveLiveEtaPayload(etaData?.websocket, anchorMs) };
    });
  }, [etaData?.websocket, riderFeedStale]);

  logger.debug('[TripTracker] current location state', {
    tripId,
    ...getLocationLogSummary(currentLocation)
  });

  const startGpsReadiness = useMemo(() => {
    const hasKnownGpsReadinessState = hasGpsSourceOptionsSharing
      || hasGpsSourceOptionsSelection
      || hasTripSelectedDevice
      || Object.prototype.hasOwnProperty.call(memoizedTripData || {}, 'location_sharing_enabled');
    const hasAssignedStartVehicle = memoizedTripData?.vehicle_id !== null
      && memoizedTripData?.vehicle_id !== undefined
      && memoizedTripData?.vehicle_id !== '';
    const hasSelectedStartVehicle = selectedStartVehicleId !== null
      && selectedStartVehicleId !== undefined
      && selectedStartVehicleId !== '';
    const required = isDriver
      && ['scheduled', 'dispatched'].includes(memoizedTripData?.status)
      && pendingGpsShareSelectionValue === null
      // GPS readiness is source/vehicle-scoped. If no shuttle has been
      // assigned or chosen yet, allow the existing vehicle recovery flow to run
      // before blocking on Share GPS state.
      && (hasAssignedStartVehicle || hasSelectedStartVehicle)
      && !startVehicleConflict
      && !startVehicleSelectionRequired
      && startVehicleOptions.length === 0
      && hasKnownGpsReadinessState;

    if (!required) {
      return { required: false, ready: true, messageKey: null };
    }

    // Start Trip is validation-only; these guards keep the driver in the
    // separate Share GPS flow until the backend-selected feed is known fresh.
    if (currentGpsSourceConflict?.isBlocking) {
      return { required: true, ready: false, messageKey: 'gpsSource.startReadiness.conflict' };
    }
    if (gpsSourceOptionsLoading) {
      return { required: true, ready: false, messageKey: 'gpsSource.startReadiness.loading' };
    }
    if (!locationSharingEnabled) {
      return { required: true, ready: false, messageKey: 'gpsSource.startReadiness.sharingOff' };
    }
    if (activeSelectedDeviceId === undefined) {
      return { required: true, ready: false, messageKey: 'gpsSource.startReadiness.sourceRequired' };
    }
    if (!selectedGpsFeedActive) {
      return { required: true, ready: false, messageKey: 'gpsSource.startReadiness.waitingForFeed' };
    }

    return { required: true, ready: true, messageKey: null };
  }, [
    activeSelectedDeviceId,
    gpsSourceOptionsLoading,
    currentGpsSourceConflict?.isBlocking,
    hasGpsSourceOptionsSelection,
    hasGpsSourceOptionsSharing,
    hasTripSelectedDevice,
    isDriver,
    locationSharingEnabled,
    memoizedTripData,
    memoizedTripData?.status,
    memoizedTripData?.vehicle_id,
    pendingGpsShareSelectionValue,
    selectedGpsFeedActive,
    selectedStartVehicleId,
    startVehicleConflict,
    startVehicleOptions.length,
    startVehicleSelectionRequired
  ]);

  const clearWaitingEtaHydrationTimer = useCallback(() => {
    if (waitingEtaHydrationTimerRef.current) {
      clearTimeout(waitingEtaHydrationTimerRef.current);
      waitingEtaHydrationTimerRef.current = null;
    }
  }, []);

  const resetWaitingEtaHydrationRetryState = useCallback(() => {
    waitingEtaHydrationRetryStateRef.current = {
      key: null,
      attempts: 0
    };
  }, []);

  const setTripStartEtaState = useCallback((state) => {
    tripStartEtaStateRef.current = {
      tripId,
      state,
      updatedAt: Date.now()
    };
  }, [tripId]);

  const clearTripStartEtaState = useCallback(() => {
    tripStartEtaStateRef.current = {
      tripId: null,
      state: null,
      updatedAt: 0
    };
  }, []);

  const isTripStartEtaHydrationSuppressed = useCallback(() => {
    const state = tripStartEtaStateRef.current;
    if (!state?.state || String(state.tripId) !== String(tripId)) {
      return false;
    }

    if (state.state === 'succeeded') {
      const isRecent = Date.now() - state.updatedAt < TRIP_START_ETA_SUCCESS_SUPPRESSION_MS;
      if (!isRecent) {
        clearTripStartEtaState();
      }
      return isRecent;
    }

    return state.state === 'starting' || state.state === 'in_flight';
  }, [clearTripStartEtaState, tripId, TRIP_START_ETA_SUCCESS_SUPPRESSION_MS]);

  const hydrateWaitingDriverEta = useCallback(async (mode) => {
    const owner = etaContinuationGenerationRef.current;
    const ownsHydration = () => owner === etaContinuationGenerationRef.current;
    if (
      isEntityMismatch ||
      memoizedTripData?.status !== 'in_progress' ||
      !isDriver ||
      !gpsActive
    ) {
      return false;
    }

    if (isTripStartEtaHydrationSuppressed()) {
      return true;
    }

    const existingWebsocketEta = getWebSocketEta();
    if (etaData?.websocket || existingWebsocketEta) {
      if (existingWebsocketEta && !etaData?.websocket) {
        acceptEtaUpdate(existingWebsocketEta, 'socket_cache_hydration');
      }
      return true;
    }

    let hydratedEta = null;

    try {
      // Missing socket/UI state is not proof that the backend lacks estimates.
      // One scoped read preserves the existing retry budget without purchasing
      // replacements or initializing again on app hydration.
      hydratedEta = await etaService.getCurrentETA(
        tripId, currentEntity, memoizedTripData, currentLocation ?? null
      );
    } catch (primaryError) {
      if (!ownsHydration()) return false;
      logger.error('[TripTracker] Waiting ETA hydration primary path failed', getSafeErrorContext(primaryError, {
        tripId,
        mode,
        hasCurrentLocation: Boolean(currentLocation)
      }));
    }

    if (!ownsHydration()) return false;
    if (!ownsHydration() || !hydratedEta) {
      return false;
    }

    if (acceptEtaUpdate(hydratedEta, 'waiting_eta_hydration')) {
      sendEtaUpdate(hydratedEta);
    }
    return true;
  }, [
    currentEntity,
    currentLocation,
    etaData?.websocket,
    getWebSocketEta,
    gpsActive,
    isDriver,
    isEntityMismatch,
    memoizedTripData,
    acceptEtaUpdate,
    isTripStartEtaHydrationSuppressed,
    sendEtaUpdate,
    tripId
  ]);

  useEffect(() => {
    if (memoizedTripData?.status !== 'in_progress' || etaData?.websocket) {
      waitingEtaHydrationRetryStateRef.current = {
        key: null,
        attempts: 0
      };
    }
  }, [memoizedTripData?.status, etaData?.websocket, tripId]);

  const scheduleWaitingEtaHydration = useCallback((mode, key) => {
    const nextAttempt = waitingEtaHydrationRetryStateRef.current.attempts + 1;
    const delayMs = nextAttempt === 1
      ? WAITING_ETA_HYDRATION_DELAY_MS
      : WAITING_ETA_HYDRATION_RETRY_DELAY_MS;

    waitingEtaHydrationTimerRef.current = setTimeout(async () => {
      if (waitingEtaHydrationRetryStateRef.current.key !== key) {
        return;
      }

      if (isTripStartEtaHydrationSuppressed()) {
        resetWaitingEtaHydrationRetryState();
        return;
      }

      waitingEtaHydrationRetryStateRef.current = {
        key,
        attempts: nextAttempt
      };

      const hydrated = await hydrateWaitingDriverEta(mode);
      if (hydrated) {
        waitingEtaHydrationRetryStateRef.current = {
          key: null,
          attempts: 0
        };
        return;
      }

      if (waitingEtaHydrationRetryStateRef.current.key !== key) {
        return;
      }

      if (waitingEtaHydrationRetryStateRef.current.attempts >= WAITING_ETA_HYDRATION_MAX_ATTEMPTS) {
        return;
      }

      scheduleWaitingEtaHydration(mode, key);
    }, delayMs);
  }, [
    WAITING_ETA_HYDRATION_DELAY_MS,
    WAITING_ETA_HYDRATION_MAX_ATTEMPTS,
    WAITING_ETA_HYDRATION_RETRY_DELAY_MS,
    hydrateWaitingDriverEta,
    isTripStartEtaHydrationSuppressed,
    resetWaitingEtaHydrationRetryState
  ]);

  useEffect(() => {
    clearWaitingEtaHydrationTimer();

    if (
      isEntityMismatch ||
      memoizedTripData?.status !== 'in_progress' ||
      !isDriver ||
      !gpsActive ||
      etaData?.websocket ||
      getWebSocketEta() ||
      isTripStartEtaHydrationSuppressed()
    ) {
      return undefined;
    }

    const mode = currentLocation ? 'with_location' : 'without_location';
    const attemptKey = `${tripId}:${memoizedTripData?.status}:${mode}`;
    if (waitingEtaHydrationRetryStateRef.current.key !== attemptKey) {
      waitingEtaHydrationRetryStateRef.current = {
        key: attemptKey,
        attempts: 0
      };
    }

    if (waitingEtaHydrationRetryStateRef.current.attempts >= WAITING_ETA_HYDRATION_MAX_ATTEMPTS) {
      return undefined;
    }

    scheduleWaitingEtaHydration(mode, attemptKey);

    return () => {
      clearWaitingEtaHydrationTimer();
    };
  }, [
    clearWaitingEtaHydrationTimer,
    currentLocation,
    etaData?.websocket,
    getWebSocketEta,
    gpsActive,
    hydrateWaitingDriverEta,
    isDriver,
    isEntityMismatch,
    isTripStartEtaHydrationSuppressed,
    memoizedTripData?.status,
    scheduleWaitingEtaHydration,
    tripId,
    WAITING_ETA_HYDRATION_MAX_ATTEMPTS
  ]);

  const refreshPreTripPlanConsumers = useCallback((planUpdateToken = null) => {
    if (!currentEntity?.entityId || !tripId || isEntityMismatch) {
      return Promise.resolve();
    }

    if (!['scheduled', 'dispatched'].includes(tripStatusRef.current)) {
      return Promise.resolve();
    }

    if (planUpdateToken != null) {
      queuedPreTripPlanUpdateTokenRef.current = planUpdateToken;
    }

    if (preTripPlanRefreshInFlightRef.current) {
      return preTripPlanRefreshInFlightRef.current;
    }

    const runRefreshLoop = async () => {
      while (
        queuedPreTripPlanUpdateTokenRef.current != null
        && queuedPreTripPlanUpdateTokenRef.current !== completedPreTripPlanUpdateTokenRef.current
      ) {
        const tokenToRefresh = queuedPreTripPlanUpdateTokenRef.current;
        const requestStatus = tripStatusRef.current;
        const [tripResponse, estimatedData] = await Promise.all([
          axios.get(`/api/trips/${currentEntity.entityId}/${tripId}`),
          fetchEstimatedRouteDuration({
            publishState: false,
            allowWhenEtaDisabled: true
          })
        ]);
        const tripPayload = unwrapAxiosPayload(tripResponse);
        const stillCurrentPreTrip = tripStatusRef.current === requestStatus
          && ['scheduled', 'dispatched'].includes(requestStatus)
          && ['scheduled', 'dispatched'].includes(tripPayload?.status);
        if (!stillCurrentPreTrip) {
          completedPreTripPlanUpdateTokenRef.current = tokenToRefresh;
          continue;
        }
        if (tripPayload) {
          setTripData(tripPayload);
        }
        if (estimatedData) {
          publishPreTripStaticEstimate(estimatedData);
        }
        completedPreTripPlanUpdateTokenRef.current = tokenToRefresh;
      }
    };

    const refreshPromise = runRefreshLoop()
      .catch((error) => {
        logger.error('[TripTracker] Failed to refresh pre-trip plan consumers', getSafeErrorContext(error, {
          tripId,
          entityId: currentEntity?.entityId
        }));
        throw error;
      })
      .finally(() => {
        preTripPlanRefreshInFlightRef.current = null;
      });

    preTripPlanRefreshInFlightRef.current = refreshPromise;
    return refreshPromise;
  }, [currentEntity?.entityId, tripId, fetchEstimatedRouteDuration, isEntityMismatch]);

  useEffect(() => {
    const planUpdateToken = latestPlanUpdate?.updatedAt;
    if (!planUpdateToken || latestPlanUpdate?.tripId !== tripId) {
      return;
    }

    if (!['scheduled', 'dispatched'].includes(memoizedTripData?.status)) {
      return;
    }

    if (completedPreTripPlanUpdateTokenRef.current === planUpdateToken) {
      return;
    }

    if (requestedPreTripPlanUpdateTokenRef.current === planUpdateToken) {
      return;
    }

    requestedPreTripPlanUpdateTokenRef.current = planUpdateToken;
    refreshPreTripPlanConsumers(planUpdateToken).catch(() => {
      if (completedPreTripPlanUpdateTokenRef.current !== planUpdateToken
        && requestedPreTripPlanUpdateTokenRef.current === planUpdateToken) {
        requestedPreTripPlanUpdateTokenRef.current = null;
      }
    });
  }, [latestPlanUpdate, tripId, memoizedTripData?.status, refreshPreTripPlanConsumers]);

  const currentEntityId = currentEntity?.entityId;
  const isPreTripStaticDistance = ['scheduled', 'dispatched'].includes(
    memoizedTripData?.status
  );
  const isStaticDistanceOnly = effectiveEtaSource === 'none';
  const hasExpectedPreTripStaticDisplayMode = Boolean(etaData?.estimated)
    && Boolean(etaData?.estimatedEtaDisabled) === isStaticDistanceOnly;
  const needsPreTripStaticHydration = isPreTripStaticDistance
    && !hasExpectedPreTripStaticDisplayMode;
  const usesActiveInitialEtaBranches = Boolean(memoizedTripData?.status)
    && !['completed', 'canceled'].includes(memoizedTripData.status)
    && !isPreTripStaticDistance;
  const hasCurrentLocation = Boolean(currentLocation);
  const driverGpsLocationAvailable = usesActiveInitialEtaBranches
    && isDriver
    && gpsActive
    && hasCurrentLocation;
  const genericEstimatedFallbackNeeded = usesActiveInitialEtaBranches
    && (!gpsActive || !hasCurrentLocation);
  // Raw location and trip-object identity are genuine request inputs only for
  // the ETA-disabled `/eta` branch. Keeping them null everywhere else prevents
  // live-location samples or unrelated trip-object allocations from becoming
  // static reference-duration hydration authority.
  const distanceOnlyLocationTrigger = usesActiveInitialEtaBranches
    && isStaticDistanceOnly
    ? currentLocation
    : null;
  const distanceOnlyTripDataTrigger = usesActiveInitialEtaBranches
    && isStaticDistanceOnly
    ? memoizedTripData
    : null;

  // Now define functions that use values from useTripTracking
  const fetchInitialETA = useCallback(async () => {
    if (isEntityMismatch) {
      return;
    }
    logger.debug('[TripTracker] fetchInitialETA started', {
      tripId,
      status: memoizedTripData?.status,
      gpsActive,
      driverGpsLocationAvailable,
      genericEstimatedFallbackNeeded
    });
    
    try {
      // Handle completed trips - fetch both actual and estimated data
      if (memoizedTripData?.status === 'completed') {
        logger.debug('[TripTracker] Fetching completed trip ETA data', { tripId });
        
        const [actualData, estimatedProjection] = await Promise.all([
          fetchActualTripDuration().catch(err => {
            logger.error('[TripTracker] Failed to fetch actual trip duration', getSafeErrorContext(err, { tripId }));
            return null;
          }),
          fetchEstimatedRouteDuration().catch(err => {
            logger.error('[TripTracker] Failed to fetch estimated route duration', getSafeErrorContext(err, { tripId }));
            return null;
          }),
        ]);
        
        logger.debug('[TripTracker] Completed trip ETA data fetched', {
          tripId,
          hasActualData: Boolean(actualData),
          hasEstimatedData: Boolean(
            normalizeEstimatedRouteProjection(estimatedProjection).estimatedData
          )
        });

        setEtaData(prevData => {
          logger.debug('[TripTracker] Preserving websocket data on completion', {
            tripId,
            hasWebsocketData: Boolean(prevData?.websocket)
          });
          return {
            ...prevData,  // Preserve existing websocket data
            actual: actualData,
            ...buildEstimatedRouteState(estimatedProjection)
          };
        });
        return;
      }
      
      // Handle canceled trips - just show canceled state
      if (memoizedTripData?.status === 'canceled') {
        logger.debug('[TripTracker] Setting canceled trip ETA state', { tripId });
        setEtaData({ canceled: true });
        return;
      }
      
      if (isPreTripStaticDistance) {
        // The planned map, card, and ETA rows share route_segments distance
        // authority before departure. Do this before consulting retained live
        // state or driver GPS: either can be stale from a previous lifecycle
        // and must not suppress static hydration.
        const estimatedProjection = await fetchEstimatedRouteDuration({
          publishState: false,
          allowWhenEtaDisabled: isStaticDistanceOnly
        });
        if (estimatedProjection) {
          publishPreTripStaticEstimate(estimatedProjection);
        }
        return;
      }

      // For active trips (scheduled, dispatched, in_progress)
      logger.debug('[TripTracker] Handling active trip ETA', {
        tripId,
        status: memoizedTripData?.status,
        gpsActive
      });
      
      // 1. First check WebSocket for existing ETA data
      const websocketEta = getWebSocketEta();
      if (websocketEta) {
        logger.debug('[TripTracker] Using existing WebSocket ETA', {
          tripId,
          ...getEtaLogSummary(websocketEta)
        });
        acceptEtaUpdate(websocketEta, 'socket_cache_lifecycle');
        return;
      }

      if (effectiveEtaSource === 'none') {
        logger.debug('[TripTracker] Entity ETA source is none, hydrating distance-only ETA state', { tripId });
        // Planned route_segments rows contain display-only timing metadata. Once
        // a trip is in progress, retain neither those pre-trip times nor their
        // marker if the distance-only live hydration fails; otherwise the
        // generic in-progress fallback can present a stale planned ETA.
        setEtaData(clearPreTripStaticEstimate);
        const disabledEtaData = await etaService.getCurrentETA(
          tripId,
          { entityId: currentEntityId },
          distanceOnlyTripDataTrigger,
          distanceOnlyLocationTrigger
        );
        if (disabledEtaData) {
          acceptEtaUpdate(disabledEtaData, 'eta_disabled_distance_hydration');
        }
        return;
      }
      
      // 2. No WebSocket ETA data available, determine next source based on GPS status
      if (isDriver && gpsActive && !driverGpsLocationAvailable) {
        // GPS is enabled but waiting for first location
        logger.debug('[TripTracker] GPS enabled but waiting for location', { tripId });
        // Don't set any ETA data - this will cause ETADisplay to show "Calculating GPS-based ETA..."
        // The fallback hydration effect will also attempt a direct initialize call.
      } else if (driverGpsLocationAvailable) {
        // GPS enabled for driver - The location update from useTripTracking will trigger the backend
        // to calculate ETA and send it via WebSocket, so we just wait for that
        logger.debug('[TripTracker] GPS enabled for driver, waiting for WebSocket ETA from location update', {
          tripId,
          driverGpsLocationAvailable
        });
        
        // Don't make a direct API call here - the location update flow handles it
        // This prevents duplicate Google Maps API calls
      } else if (genericEstimatedFallbackNeeded) {
        // No GPS - use estimated route duration for any role
        logger.debug('[TripTracker] No GPS available, fetching estimated route duration', { tripId });
        const estimatedProjection = await fetchEstimatedRouteDuration();
        if (estimatedProjection) {
          setEtaData(buildEstimatedRouteState(estimatedProjection));
        }
      }
      
    } catch (error) {
      logger.error('[TripTracker] Error fetching initial ETA', getSafeErrorContext(error, { tripId }));
      // Don't show toast for initial fetch failures, just log
    }
  }, [
    tripId,
    currentEntityId,
    isDriver,
    memoizedTripData?.status,
    gpsActive,
    driverGpsLocationAvailable,
    genericEstimatedFallbackNeeded,
    distanceOnlyLocationTrigger,
    distanceOnlyTripDataTrigger,
    getWebSocketEta,
    acceptEtaUpdate,
    fetchActualTripDuration,
    fetchEstimatedRouteDuration,
    effectiveEtaSource,
    isEntityMismatch,
    isPreTripStaticDistance,
    isStaticDistanceOnly,
    publishPreTripStaticEstimate,
  ]);
    
  // Updated trip action handlers to properly manage ETA after status changes
  const openVehicleSelectionRecovery = (recoveryPayload = {}, archivedType = null, pendingShareSelectionValue = null) => {
    const activeVehicles = Array.isArray(recoveryPayload.activeVehicles)
      ? recoveryPayload.activeVehicles
      : [];
    setStartVehicleConflict(null);
    setStartVehicleOptions(activeVehicles);
    setSelectedStartVehicleId(activeVehicles[0]?.id || null);
    setStartVehicleSelectionRequired(true);
    setPendingStartArchivedType(archivedType ?? pendingStartArchivedType ?? null);
    // Share GPS uses the same vehicle picker, but confirmation assigns the
    // shuttle and retries sharing instead of starting the trip.
    setPendingGpsShareSelectionValue(pendingShareSelectionValue);
    setShowStartDialog(true);
  };

  const wrappedStartTrip = async (archivedType, startOptions = {}) => {
    setTripStartEtaState('starting');
    clearWaitingEtaHydrationTimer();
    resetWaitingEtaHydrationRetryState();

    try {
      const requestOptions = {};
      const requestedArchivedType = archivedType ?? pendingStartArchivedType ?? null;
      const effectiveVehicleId = startOptions.vehicleId
        ?? selectedStartVehicleId
        ?? memoizedTripData?.vehicle_id
        ?? null;

      if (effectiveVehicleId) {
        requestOptions.vehicleId = Number(effectiveVehicleId);
      }
      if (startOptions.overrideVehicleConflict) {
        requestOptions.overrideVehicleConflict = true;
      }

      setPendingStartArchivedType(requestedArchivedType);
      const resolvedType = Object.keys(requestOptions).length > 0
        ? await onStartTrip(tripId, requestedArchivedType, requestOptions)
        : await onStartTrip(tripId, requestedArchivedType);
      const effectiveArchivedType = resolvedType ?? requestedArchivedType ?? null;
      setStartVehicleOptions([]);
      setSelectedStartVehicleId(null);
      setStartVehicleConflict(null);
      setStartVehicleSelectionRequired(false);
      setPendingStartArchivedType(null);
      setPendingGpsShareSelectionValue(null);
      logger.debug('[TripAction] Trip started', {
        tripId,
        archivedType: effectiveArchivedType
      });

      let initializedEta = null;

      // After starting trip, if GPS is active, get fresh ETA and send through WebSocket
      // before flipping the local status. This avoids the temporary in-progress
      // "Calculating ETA" gap when the initialize response is already the next
      // authoritative payload for the trip.
      if (gpsActive) {
        logger.debug('[TripAction] Trip started with GPS active, updating ETA', { tripId });
        setTripStartEtaState('in_flight');
        try {
          initializedEta = await etaService.initializeETA(tripId, currentLocation ?? null, {
            ...currentEntity,
            eta_source: entitySettings?.eta_source || currentEntity.eta_source
          });
          if (initializedEta) {
            setTripStartEtaState('succeeded');
            clearWaitingEtaHydrationTimer();
            resetWaitingEtaHydrationRetryState();
          } else {
            clearTripStartEtaState();
          }
        } catch (etaInitError) {
          logger.error('[TripAction] ETA initialization after trip start failed', getSafeErrorContext(etaInitError, {
            tripId,
            hasCurrentLocation: Boolean(currentLocation)
          }));
          clearTripStartEtaState();
        }
      } else {
        clearTripStartEtaState();
      }

      // Update local status once the immediate start-time ETA hydration attempt
      // has completed, so the UI can transition directly into a populated
      // in-progress state when initialization succeeds.
      if (effectiveEtaSource === 'none') {
        // Clear planned ETA rows before changing lifecycle state. A failed
        // distance-only hydration must leave no scheduled timing fallback for
        // an in-progress trip that was started locally.
        setEtaData(clearPreTripStaticEstimate);
      }
      setTripData((prev) => ({
        ...prev,
        status: 'in_progress',
        selected_archived_type: effectiveArchivedType
      }));

      if (initializedEta) {
        logger.debug('[TripAction] Sending fresh ETA through WebSocket after trip start', {
          tripId,
          ...getEtaLogSummary(initializedEta)
        });
        if (acceptEtaUpdate(initializedEta, 'trip_start')) {
          sendEtaUpdate(initializedEta);
        }
      } else if (gpsActive && !currentLocation) {
        // The shared lifecycle effect deliberately waits while browser GPS is
        // enabled but has no first location. Preserve this distinct fallback
        // when start-time initialization returned no live ETA; GPS-inactive
        // starts are hydrated once by the lifecycle effect instead.
        logger.debug('[TripAction] GPS enabled without a location after start, using estimated data', { tripId });
        const estimatedProjection = await fetchEstimatedRouteDuration();
        if (estimatedProjection) {
          setEtaData(buildEstimatedRouteState(estimatedProjection));
        }
      }
    } catch (error) {
      clearTripStartEtaState();
      const recoveryPayload = error?.response?.data || error?.responseBody || {};
      if (recoveryPayload.code === 'VEHICLE_REQUIRED') {
        openVehicleSelectionRecovery(recoveryPayload, archivedType, null);
        return;
      }
      if (recoveryPayload.code === 'VEHICLE_ACTIVE_TRIP_CONFLICT') {
        setStartVehicleOptions([]);
        setStartVehicleSelectionRequired(false);
        setPendingGpsShareSelectionValue(null);
        setStartVehicleConflict({
          blockingTrips: Array.isArray(recoveryPayload.blockingTrips)
            ? recoveryPayload.blockingTrips
            : []
        });
        setPendingStartArchivedType(archivedType ?? pendingStartArchivedType ?? null);
        setShowStartDialog(true);
        return;
      }
      if (recoveryPayload.code === 'GPS_DEVICE_TRIP_CONFLICT') {
        const conflictState = buildGpsSourceConflictState(recoveryPayload, 'start', currentGpsSourceValue);
        if (conflictState) {
          setGpsSourceConflict(conflictState);
          setShowGpsSourceChooser(true);
        }
        return;
      }
      logger.error('[TripAction] Start error', getSafeErrorContext(error, { tripId }));
      toast.error(t(GPS_START_ERROR_KEYS[recoveryPayload.code] || 'failedToStartTrip'));
    }
  };
  
  const handleStartTripRequest = () => {
    // Confirm remains disabled while GPS readiness is blocked, so surface the
    // transient error on the driver's reachable Start action while the dialog
    // continues to provide persistent recovery guidance.
    if (startGpsReadiness.required && !startGpsReadiness.ready) {
      toast.error(t(startGpsReadiness.messageKey || 'gpsSource.startReadiness.waitingForFeed'));
    }
    setStartVehicleOptions([]);
    setSelectedStartVehicleId(null);
    setStartVehicleConflict(null);
    setStartVehicleSelectionRequired(false);
    setPendingStartArchivedType(null);
    setPendingGpsShareSelectionValue(null);
    // Always show the start dialog
    // The dialog will show simple confirmation if 0-1 types available
    // or route type selection if 2+ types available
    setShowStartDialog(true);
  };

  const handleStartVehicleChange = (vehicleId) => {
    setSelectedStartVehicleId(vehicleId);
    if (showStartDialog && vehicleId) {
      loadGpsSourceOptions(vehicleId);
    }
  };

  const handleInProgressGpsSourceChange = async (selectionValue) => {
    if (gpsSourceSwitchInFlightRef.current) {
      return;
    }

    const sharingEnabled = selectionValue !== 'off';
    const selectedDeviceId = resolveGpsSourceRequestValue(selectionValue);
    const requestBody = sharingEnabled
      ? {
          selected_device_id: selectedDeviceId,
          sharing_enabled: true
        }
      : {
          sharing_enabled: false
        };

    gpsSourceSwitchInFlightRef.current = true;
    setIsGpsSourceSwitching(true);
    try {
      const response = await axios.put(`/api/trips/${currentEntity.entityId}/${tripId}/switch-gps-source`, requestBody);
      const responseData = response?.data || {};
      const nextSelectedDeviceId = responseData.selected_device_id ?? null;
      const nextSourceType = responseData.source_type || (nextSelectedDeviceId ? 'device' : 'phone');
      const nextSharingEnabled = responseData.location_sharing_enabled === true;

      setUsePhoneGPS(nextSharingEnabled && nextSourceType === 'phone');
      setTripData((prev) => ({
        ...prev,
        selected_device_id: nextSelectedDeviceId,
        location_sharing_enabled: nextSharingEnabled
      }));
      setGpsSourceOptions((prev) => prev
        ? {
            ...prev,
            current_selected_device_id: nextSelectedDeviceId,
            location_sharing_enabled: nextSharingEnabled
          }
        : prev);
      const nextSourceValue = !nextSharingEnabled
        ? 'off'
        : nextSourceType === 'phone'
          ? 'phone'
          : (
              scopedGpsSourceOptions?.default_device?.id
              && Number(scopedGpsSourceOptions.default_device.id) === Number(nextSelectedDeviceId)
                ? 'default'
                : `device:${nextSelectedDeviceId}`
            );
      setPendingGpsSourceValue(nextSourceValue);
      setGpsSourceConflict(null);
    } catch (error) {
      const recoveryPayload = error?.response?.data || error?.responseBody || {};
      if (recoveryPayload.code === 'GPS_SOURCE_VEHICLE_REQUIRED') {
        openVehicleSelectionRecovery(recoveryPayload, pendingStartArchivedType, selectionValue);
        return;
      }
      if (recoveryPayload.code === 'GPS_DEVICE_TRIP_CONFLICT') {
        const conflictState = buildGpsSourceConflictState(recoveryPayload, 'switch', selectionValue);
        if (conflictState) {
          setGpsSourceConflict(conflictState);
          setShowGpsSourceChooser(true);
        }
        return;
      }
      logger.error('[TripTracker] Failed to switch GPS source', getSafeErrorContext(error, {
        tripId,
        entityId: currentEntity?.entityId,
        selectedDeviceId
      }));
      toast.error(t('gpsSource.switchFailed'));
    } finally {
      gpsSourceSwitchInFlightRef.current = false;
      setIsGpsSourceSwitching(false);
    }
  };

  const assignVehicleForPendingGpsShare = async (startOptions = {}) => {
    const selectionValue = pendingGpsShareSelectionValue;
    const effectiveVehicleId = startOptions.vehicleId
      ?? selectedStartVehicleId
      ?? null;

    if (!selectionValue || !effectiveVehicleId) {
      toast.error(t('gpsSource.switchFailed'));
      return;
    }

    const payload = { vehicle_id: Number(effectiveVehicleId) };
    if (startOptions.overrideVehicleConflict) {
      payload.override_vehicle_conflict = true;
    }

    try {
      const assignedDriverId = memoizedTripData?.driver_id ?? memoizedTripData?.driverId ?? null;
      const driverAssignmentSource = memoizedTripData?.driver_assignment_source
        ?? memoizedTripData?.driverAssignmentSource
        ?? null;
      const shouldClaimTripForShare = assignedDriverId === null
        || assignedDriverId === undefined
        || AUTO_DRIVER_ASSIGNMENT_SOURCES.has(driverAssignmentSource);

      if (shouldClaimTripForShare) {
        await axios.post(`/api/trips/${currentEntity.entityId}/${tripId}/assign`, payload);
      } else {
        // The claim endpoint intentionally rejects manually assigned rows. For
        // Share GPS recovery on an already-owned trip, only confirm the shuttle
        // so source sharing can be retried without rewriting driver ownership.
        await axios.put(`/api/trips/${currentEntity.entityId}/${tripId}/vehicle-assignment`, payload);
      }
      setStartVehicleOptions([]);
      setSelectedStartVehicleId(null);
      setStartVehicleConflict(null);
      setStartVehicleSelectionRequired(false);
      setPendingGpsShareSelectionValue(null);
      setTripData((prev) => ({
        ...prev,
        vehicle_id: Number(effectiveVehicleId),
        status: shouldClaimTripForShare && prev?.status === 'scheduled' ? 'dispatched' : prev?.status
      }));
      await loadGpsSourceOptions(Number(effectiveVehicleId));
      await handleInProgressGpsSourceChange(selectionValue);
    } catch (error) {
      const recoveryPayload = error?.response?.data || error?.responseBody || {};
      if (recoveryPayload.code === 'VEHICLE_ACTIVE_TRIP_CONFLICT') {
        setStartVehicleOptions([]);
        setStartVehicleSelectionRequired(false);
        setStartVehicleConflict({
          blockingTrips: Array.isArray(recoveryPayload.blockingTrips)
            ? recoveryPayload.blockingTrips
            : []
        });
        setPendingGpsShareSelectionValue(selectionValue);
        setShowStartDialog(true);
        return;
      }
      logger.error('[TripTracker] Failed to assign vehicle before sharing GPS', getSafeErrorContext(error, {
        tripId,
        entityId: currentEntity?.entityId,
        vehicleId: effectiveVehicleId
      }));
      toast.error(t('gpsSource.switchFailed'));
    }
  };

  const handleStartDialogConfirm = (archivedType, startOptions = {}) => {
    if (pendingGpsShareSelectionValue !== null) {
      assignVehicleForPendingGpsShare(startOptions);
    } else if (startGpsReadiness.required && !startGpsReadiness.ready) {
      toast.error(t(startGpsReadiness.messageKey || 'gpsSource.startReadiness.waitingForFeed'));
    } else {
      wrappedStartTrip(archivedType, startOptions);
    }
    setShowStartDialog(false);
  };
  
  const wrappedCompleteTrip = async () => {
    if (completeActionInFlightRef.current || isCompletingTrip) {
      return;
    }

    completeActionInFlightRef.current = true;
    setIsCompleteActionPending(true);

    try {
      const result = await onCompleteTrip(tripId);
      if (result?.ignored) {
        return;
      }
      logger.debug('[TripAction] Trip completed', { tripId });
      
      // Update local status immediately
      setTripData((prev) => ({ ...prev, status: 'completed' }));
      
      // Status will be updated via WebSocket, which will trigger fetchInitialETA() automatically
      logger.debug('[TripAction] Trip completion initiated, waiting for WebSocket status update', { tripId });
      
    } catch (error) {
      logger.error('[TripAction] Complete error', getSafeErrorContext(error, { tripId }));
      toast.error(t('failedToCompleteTrip'));
    } finally {
      completeActionInFlightRef.current = false;
      setIsCompleteActionPending(false);
    }
  };
  
  const wrappedCancelTrip = async (reason) => {
    try {
      await onCancelTrip(tripId, reason);
      logger.debug('[TripAction] Trip canceled', {
        tripId,
        hasReason: Boolean(reason)
      });
      
      // Update local status immediately
      setTripData((prev) => ({ ...prev, status: 'canceled' }));
      
      // After canceling trip, show canceled state
      setEtaData({ canceled: true });
      
    } catch (error) {
      logger.error('[TripAction] Cancel error', getSafeErrorContext(error, { tripId }));
      toast.error(t('failedToCancelTrip'));
    }
  };
  
  // Store the sendStatusUpdate function in the ref
  useEffect(() => {
    sendStatusUpdateRef.current = sendStatusUpdate;
  }, [sendStatusUpdate]);

  const formatRoute = useMemo(() => {
    if (routeDisplayStops.length === 0) return null;

    // Keep all display stops so the tracking map stays aligned with the ETA list,
    // including skipped stops that should remain visible in route order.
    const waypoints = routeDisplayStops
      .map(stop => ({
      // Core identifiers
      id: stop.waypoint_id,
      waypoint_id: stop.waypoint_id,
      name: stop.waypoint_name,
      
      // Location data
      latitude: stop.latitude,
      longitude: stop.longitude,
      address: stop.address,
      
      // Sequencing (support reorderable waypoints)
      sequence_number: stop.sequence_number,
      effective_sequence: stop.effective_sequence, // Custom order from trip_waypoint_order
      is_skipped: stop.is_skipped, // Non-required waypoint without bookings
      
      // Type information
      point_type: stop.point_type, // 'stop', 'waypoint'
      stop_position: stop.stop_position, // 'start', 'end', 'intermediate'
      
      // Capabilities
      can_pickup: stop.can_pickup,
      can_dropoff: stop.can_dropoff,
      is_required: stop.is_required,
      
      // For display logic - determine the primary type for this location
      type: stop.stop_position === 'start' ? 'start' 
          : stop.stop_position === 'end' ? 'end'
          : stop.pickup_count > 0 && stop.dropoff_count > 0 ? 'both'
          : stop.pickup_count > 0 ? 'pickup'
          : stop.dropoff_count > 0 ? 'dropoff'
          : stop.point_type === 'stop' ? 'stop'
          : 'waypoint',
      
      // Booking counts (for driver view)
      pickup_count: stop.pickup_count || 0,
      pickup_riders: stop.pickup_riders || 0,
      pickup_luggages: stop.pickup_luggages || 0,
      dropoff_count: stop.dropoff_count || 0,
      dropoff_riders: stop.dropoff_riders || 0,
      dropoff_luggages: stop.dropoff_luggages || 0,
      
      // User-specific flags
      is_user_pickup: stop.is_user_pickup || false,
      is_user_dropoff: stop.is_user_dropoff || false,
      
      // Duration information (if available)
      stay_duration_minutes: stop.stay_duration_minutes,

      // Cumulative distance from start (pre-trip display)
      cumulative_distance_from_start_meters: stop.cumulative_distance_from_start_meters,
      cumulative_duration_from_start_seconds: stop.cumulative_duration_from_start_seconds,
    }));

    // Sort waypoints by effective_sequence (reorderable) or sequence_number (default)
    // Keep skipped waypoints visible for display (dimmed with "No Riders, Skipped" label)
    const sortedWaypoints = [...waypoints]
      .sort((a, b) => {
        const seqA = a.effective_sequence ?? a.sequence_number;
        const seqB = b.effective_sequence ?? b.sequence_number;
        return seqA - seqB;
      });

    // Replace the waypoints array with sorted/filtered version
    waypoints.length = 0;
    waypoints.push(...sortedWaypoints);

    return {
      waypoints,
      // Additional metadata for the map
      total_stops: waypoints.filter(w => w.point_type === 'stop').length,
      has_waypoints: waypoints.some(w => w.point_type === 'waypoint'),
    };
  }, [routeDisplayStops]);

  const initialMapCenter = useMemo(() => {
    if (formatRoute?.waypoints?.[0]) {
      return [
        parseFloat(formatRoute.waypoints[0].latitude),
        parseFloat(formatRoute.waypoints[0].longitude),
      ];
    }
    return [43.81958975, -79.3971357]; // Default to Don Mills Rd? need to change this
  }, [formatRoute]);

 
  useEffect(() => {
    if (!memoizedTripData?.status || isEntityMismatch) {
      return;
    }

    // ETA response objects are output state, not refresh authority. Pre-trip
    // publication flips this scalar guard to satisfied; active responses and
    // standard live-location samples leave every effect input unchanged.
    if (isPreTripStaticDistance && !needsPreTripStaticHydration) {
      return;
    }

    fetchInitialETA();
  }, [
    memoizedTripData?.status,
    fetchInitialETA,
    isEntityMismatch,
    isPreTripStaticDistance,
    needsPreTripStaticHydration
  ]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  const findNearestWaypoint = (location, waypoints) => {
    let nearest = null;
    let minDistance = Infinity;

    waypoints.forEach((waypoint) => {
      const distance = calculateDistance(
        location.lat,
        location.lng,
        parseFloat(waypoint.latitude),
        parseFloat(waypoint.longitude)
      );
      if (distance < minDistance) {
        minDistance = distance;
        nearest = { ...waypoint, distance };
      }
    });

    return nearest;
  };

  const isAtWaypoint = (location, waypoint) => {
    const distance = calculateDistance(
      location.lat,
      location.lng,
      parseFloat(waypoint.latitude),
      parseFloat(waypoint.longitude)
    );
    return distance <= WAYPOINT_RADIUS;
  };

  // Find next waypoint using effective_sequence (reorderable) or sequence_number
  const findNextWaypoint = (currentWaypoint, waypoints) => {
    const currentSeq = currentWaypoint.effective_sequence ?? currentWaypoint.sequence_number;
    const currentIndex = waypoints.findIndex(w => {
      const wpSeq = w.effective_sequence ?? w.sequence_number;
      return wpSeq === currentSeq;
    });
    if (currentIndex < waypoints.length - 1) {
      return waypoints[currentIndex + 1];
    }
    return null;
  };

  useEffect(() => {
    if (memoizedTripData?.status !== 'in_progress' || !etaData?.waypoints || !currentLocation) {
      return;
    }

    const checkETADeviation = async () => {
      logger.debug('[TripTracker] Checking ETA deviation', {
        tripId,
        ...getLocationLogSummary(currentLocation),
        hasLastWaypointTimestamp: Boolean(lastWaypointTimestamp),
        nextExpectedWaypointId: nextExpectedWaypoint?.waypoint_id ?? null,
      });

      if (
        lastCheckedLocation &&
        calculateDistance(
          currentLocation.lat,
          currentLocation.lng,
          lastCheckedLocation.lat,
          lastCheckedLocation.lng
        ) < 10
      ) {
        return;
      }

      const now = new Date();
      let shouldUpdate = false;
      let nearestWaypoint = null;

      try {
        nearestWaypoint = findNearestWaypoint(currentLocation, etaData.waypoints);

        if (nearestWaypoint && isAtWaypoint(currentLocation, nearestWaypoint)) {
          logger.debug('[TripTracker] At waypoint during ETA deviation check', {
            tripId,
            waypointId: nearestWaypoint.waypoint_id ?? nearestWaypoint.id ?? null,
            distanceMeters: Number.isFinite(Number(nearestWaypoint.distance)) ? Math.round(Number(nearestWaypoint.distance)) : null
          });
          if (nearestWaypoint.dynamic_arrival_time) {
            const actualArrival = now;
            const predictedArrival = new Date(nearestWaypoint.dynamic_arrival_time);
            const timeDiff = Math.abs(actualArrival - predictedArrival);
            const deviation = lastWaypointTimestamp
              ? timeDiff / (predictedArrival - lastWaypointTimestamp)
              : 0;

            logger.debug('[TripTracker] Time deviation calculated', {
              tripId,
              hasActualArrival: Boolean(actualArrival),
              hasPredictedArrival: Boolean(predictedArrival),
              deviation,
            });

            if (deviation > DEVIATION_THRESHOLD) {
              logger.debug('[TripTracker] Deviation exceeds threshold', {
                tripId,
                deviation
              });
              shouldUpdate = true;
            }
          }

          setLastWaypointTimestamp(now);
          const next = findNextWaypoint(nearestWaypoint, etaData.waypoints);
          setNextExpectedWaypoint(next);
          logger.debug('[TripTracker] Set next expected waypoint', {
            tripId,
            waypointId: next?.waypoint_id ?? next?.id ?? null
          });
        }

        if (nextExpectedWaypoint && lastWaypointTimestamp) {
          if (nextExpectedWaypoint.dynamic_arrival_time) {
            const expectedArrival = new Date(nextExpectedWaypoint.dynamic_arrival_time);
            const timeElapsed = now - lastWaypointTimestamp;

            logger.debug('[TripTracker] ETA deviation time check', {
              tripId,
              timeElapsed,
              hasExpectedArrival: Boolean(expectedArrival),
              threshold: MAX_TIME_WITHOUT_WAYPOINT,
            });

            if (timeElapsed > MAX_TIME_WITHOUT_WAYPOINT && now > expectedArrival) {
              logger.debug('[TripTracker] Exceeded max time without reaching waypoint', { tripId });
              shouldUpdate = true;
            }
          }
        }

        if (shouldUpdate) {
          logger.debug('[TripTracker] ETA deviation detected; next location update will refresh ETA', { tripId });
          // ETA updates now only happen through location updates to prevent duplicate API calls
          // The next GPS location update will trigger the ETA recalculation if needed
        }

        setLastCheckedLocation(currentLocation);
      } catch (error) {
        logger.error('[TripTracker] Error checking ETA deviation', getSafeErrorContext(error, { tripId }));
      }
    };

    const interval = setInterval(checkETADeviation, CHECK_INTERVAL);
    checkETADeviation();

    return () => clearInterval(interval);
  }, [
    memoizedTripData?.status,
    currentLocation,
    etaData?.waypoints,
    tripId,
    currentEntity,
    lastWaypointTimestamp,
    nextExpectedWaypoint,
    lastCheckedLocation,
    sendEtaUpdate,
  ]);

  // Event listener for ETA refresh requests from ETADisplay after app resume
  useEffect(() => {
    let inFlight = false;
    let disposed = false;
    const handleETARefreshRequest = async (event) => {
      if (inFlight || disposed) return;
      const owner = etaContinuationGenerationRef.current;
      const ownsRequest = () => !disposed && owner === etaContinuationGenerationRef.current;
      logger.debug('[TripTracker] Received ETA refresh request', {
        tripId,
        requestTripId: event.detail?.tripId ?? null,
        requestEntityId: event.detail?.entityId ?? null,
        reason: event.detail?.reason || null
      });
      
      const { tripId: requestTripId, entityId: requestEntityId, reason } = event.detail;
      
      // Verify this request is for our current trip
      if (requestTripId !== tripId || requestEntityId !== currentEntity?.entityId) {
        logger.debug('[TripTracker] ETA refresh request not for this trip, ignoring', {
          tripId,
          requestTripId,
          requestEntityId
        });
        return;
      }
      
      // Only refresh for in-progress trips
      if (memoizedTripData?.status !== 'in_progress') {
        logger.debug('[TripTracker] Cannot refresh ETA for current status', {
          tripId,
          status: memoizedTripData?.status
        });
        return;
      }
      
      inFlight = true;
      try {
        logger.debug('[TripTracker] Fetching fresh ETA data from backend', {
          tripId,
          reason,
          gpsActive,
          isDriver,
          hasCurrentLocation: Boolean(currentLocation)
        });
        
        // Resuming the app asks for existing state. Only the explicit map
        // Refresh handler below authorizes replacement provider metrics.
        const freshEtaData = await etaService.getCurrentETA(tripId, currentEntity, memoizedTripData, currentLocation);
        if (!ownsRequest()) return;
        
        if (freshEtaData) {
          logger.debug('[TripTracker] Successfully fetched fresh ETA data from backend', {
            tripId,
            ...getEtaLogSummary(freshEtaData)
          });
          
          const refreshedEta = {
            ...freshEtaData,
            _refreshed_after_resume: true,
            _refresh_timestamp: new Date().toISOString()
          };
          if (acceptEtaUpdate(refreshedEta, 'app_resume')) {
            sendEtaUpdate(freshEtaData);
          }
        } else {
          logger.warn('[TripTracker] No fresh ETA data received from backend', { tripId });
        }
        
      } catch (error) {
        if (!ownsRequest()) return;
        logger.error('[TripTracker] Failed to fetch fresh ETA data after app resume', getSafeErrorContext(error, {
          tripId,
          reason
        }));
        // Don't show user error for background refresh failures
      } finally {
        inFlight = false;
      }
    };
    
    // Add event listener
    window.addEventListener('requestETARefresh', handleETARefreshRequest);
    
    // Cleanup on unmount
    return () => {
      disposed = true;
      window.removeEventListener('requestETARefresh', handleETARefreshRequest);
    };
  }, [tripId, currentEntity?.entityId, memoizedTripData?.status, currentLocation, gpsActive, isDriver, sendEtaUpdate, acceptEtaUpdate]);
  const handleForceRefresh = async () => {
    if (!currentLocation || isRefreshing) return;

    try {
      setIsRefreshing(true);
      const updatedEta = await etaService.forceRefreshETA(tripId, currentLocation, currentEntity);
      if (updatedEta) {
        if (acceptEtaUpdate(updatedEta, 'manual_force_refresh')) {
          sendEtaUpdate(updatedEta);
        }
        toast.success(t('etasRefreshedSuccessfully'));
      }
    } catch (error) {
      logger.error('[TripTracker] Failed to refresh ETAs', getSafeErrorContext(error, { tripId }));
      toast.error(t('failedToRefreshEtas'));
    } finally {
      setIsRefreshing(false);
    }
  };

  const [existingReferenceTypes, setExistingReferenceTypes] = useState([]);

  const handleCreateReferenceRouteClick = async () => {
    try {
      // Step 1: Check if trip has reordered waypoints (shortcut edges)
      const shortcutResult = await shortcutSegmentService.detectShortcutEdges(
        currentEntity.entityId,
        tripId
      );

      if (shortcutResult.success && shortcutResult.hasReorderedWaypoints && shortcutResult.shortcutEdges?.length > 0) {
        // Trip has shortcuts - show shortcut dialog first
        setShortcutEdgesData(shortcutResult);
        setShowShortcutSegmentsDialog(true);
        return;
      }

      // Step 2: No shortcuts, proceed directly to reference route dialog
      const response = await axios.get(
        `/api/trips/${currentEntity.entityId}/${tripId}/check-reference-routes`
      );

      // Always show the dialog, pass existing types if any
      setExistingReferenceTypes(response.data.existingTypes || []);
      setShowReferenceRouteDialog(true);
    } catch (error) {
      logger.error('Error checking reference routes', getSafeErrorContext(error, {
        tripId,
        entityId: currentEntity?.entityId
      }));
      // Still show dialog even on error, with empty existing types
      setExistingReferenceTypes([]);
      setShowReferenceRouteDialog(true);
    }
  };

  // Handle shortcut segments dialog close
  const handleShortcutSegmentsClose = async (result) => {
    setShowShortcutSegmentsDialog(false);
    setShortcutEdgesData(null);

    // After shortcuts are handled (saved or skipped), show reference route dialog
    // for whole-route sequential data
    try {
      const response = await axios.get(
        `/api/trips/${currentEntity.entityId}/${tripId}/check-reference-routes`
      );
      setExistingReferenceTypes(response.data.existingTypes || []);
      setShowReferenceRouteDialog(true);
    } catch (error) {
      logger.error('Error checking reference routes', getSafeErrorContext(error, {
        tripId,
        entityId: currentEntity?.entityId
      }));
      setExistingReferenceTypes([]);
      setShowReferenceRouteDialog(true);
    }
  };

  const handleCreateReferenceRoute = async (action) => {
    if (!action) {
      setShowReferenceRouteDialog(false);
      return;
    }

    setReferenceRouteLoading(true);
    try {
      const requestBody = {
        replaceExisting: existingReferenceTypes.includes(action),
        routeType: action
      };

      await axios.post(
        `/api/trips/${currentEntity.entityId}/${tripId}/create-reference-route`,
        requestBody
      );

      toast.success(t('referenceRouteCreatedSuccessfully'));
      setShowReferenceRouteDialog(false);
      setExistingReferenceTypes([]); // Clear existing types after success
    } catch (error) {
      logger.error('Error creating reference route', getSafeErrorContext(error, {
        tripId,
        entityId: currentEntity?.entityId,
        routeType: action
      }));
      toast.error(t('failedToCreateReferenceRoute'));
    } finally {
      setReferenceRouteLoading(false);
    }
  };

  const handleViewBoardingPass = () => {
    // Format booking data for modal display (similar to BookingListPanel)
    const activeUserBooking = selectedRiderBooking || tripData?.user_booking;
    if (!activeUserBooking) return;

    // Get translated waypoint names (uses cache from ETADisplay if already fetched)
    const pickupLocationName = getTranslatedWaypointName(
      activeUserBooking.pickup_stop_id,
      activeUserBooking.pickup_stop_name,
      waypointTranslations,
      entitySettings,
      currentEntity
    );

    const dropoffLocationName = getTranslatedWaypointName(
      activeUserBooking.dropoff_stop_id,
      activeUserBooking.dropoff_stop_name,
      waypointTranslations,
      entitySettings,
      currentEntity
    );

    const formattedBooking = {
      id: activeUserBooking.booking_id,
      entity_id: currentEntity?.entityId,
      tripId: tripData.id || tripData.trip_id,
      riders: activeUserBooking.riders,
      luggages: activeUserBooking.luggages,
      formattedDate: formatDate(tripData.trip_date, dateFormat),
      formattedTime: formatTimeString(tripData.departure_time, timeFormat),
      pickupLocationName,
      dropoffLocationName
    };

    setSelectedBoardingBooking(formattedBooking);
    setBoardingPassModalOpen(true);
  };

  // Format speed based on user's distance unit preference
  const formatSpeed = (speedKmh) => {
    if (speedKmh === 0 || speedKmh === null || typeof speedKmh === 'undefined' || isNaN(speedKmh)) {
      return `0 ${distanceUnit === 'mi' ? t('mph') : t('kmh')}`;
    }

    if (distanceUnit === 'mi') {
      // Convert km/h to mph (1 km/h = 0.621371 mph)
      const speedMph = Math.round(speedKmh * 0.621371);
      return `${speedMph} ${t('mph')}`;
    } else {
      // Display in km/h
      return `${Math.round(speedKmh)} ${t('kmh')}`;
    }
  };

  const ConnectionStatus = () => {
    logger.debug('[TripTracker] Connection status render', {
      tripId,
      connectionStatus
    });
    const statusColors = {
      connected: 'bg-green-500',
      connecting: 'bg-yellow-500',
      reconnecting: 'bg-yellow-500',
      stale_reconnecting: 'bg-orange-500',
      disconnected: 'bg-red-500',
      error: 'bg-red-600',
      access_denied: 'bg-red-600',
    };

    const statusMessages = {
      connected: t('webSocketConnected'),
      connecting: t('webSocketConnecting'),
      reconnecting: t('webSocketReconnecting'),
      stale_reconnecting: `${t('webSocketReconnecting')} (stale)`,
      disconnected: t('WebSocket Disconnected'),
      error: t('WebSocket Connection Error'),
      access_denied: t('accessDenied'),
    };

    return (
      <div className="flex items-center gap-2">
        <div className={cn(
          "flex items-center gap-2 px-3 py-1 rounded-full",
          background.secondary
        )}>
          <div
            className={`w-2 h-2 rounded-full ${statusColors[connectionStatus || 'disconnected']}`}
          />
          <span className={cn("text-sm font-medium", text.primary)}>
            {statusMessages[connectionStatus || 'disconnected']}
          </span>
        </div>
      </div>
    );
  };

  const isRiderView = !isDriver;
  const riderContentInsetClassName = isRiderView ? 'px-4 sm:px-0' : '';
  const gpsSourceApplyDisabled = gpsSourceOptionsLoading
    || isGpsSourceSwitching
    || displayedGpsSourceValue === currentGpsSourceValue;
  const conflictTrips = currentGpsSourceConflict?.isBlocking
    ? currentGpsSourceConflict.blockingTrips
    : (currentGpsSourceConflict?.warningTrips || []);
  const showGpsSourceConflict = Boolean(currentGpsSourceConflict);
  const chooseGpsConflictRecovery = (selectionValue) => {
    setShowGpsSourceChooser(true);
    updatePendingGpsSourceValue(selectionValue);
  };

  return (
    <div>
      <div className={cn(
        "w-full",
        isRiderView ? "max-w-none rounded-none bg-transparent" : "rounded-lg max-w-4xl",
        !isRiderView && components.card.base,
        !isRiderView && shadow.lg
      )}>
        {((isDriver && memoizedTripData.status !== 'completed' && memoizedTripData.status !== 'canceled') || 
          (isAdmin && memoizedTripData.status === 'completed')) && (
          <>
            {isDriver && memoizedTripData.status !== 'completed' && (
              <div className="flex flex-wrap items-start gap-3 p-6">
                {['scheduled', 'dispatched', 'in_progress'].includes(memoizedTripData.status) && (
                  <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                    <button
                      type="button"
                      aria-expanded={showGpsSourceChooser || showGpsSourceConflict}
                      aria-controls="trip-gps-source-chooser"
                      onClick={() => setShowGpsSourceChooser((current) => !current)}
                      className={cn('px-4 py-2 rounded flex shrink-0 items-center gap-2', components.button.secondary)}
                    >
                      <Navigation2 className="h-4 w-4" />
                      {t('gpsSource.shareGps')}
                    </button>
                    {showGpsSourceChooser && (
                      <div id="trip-gps-source-chooser" className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                        <label className="flex min-w-0 max-w-full flex-1 flex-wrap items-center gap-2">
                          <span className={cn('text-sm font-medium', text.primary)}>
                            {t('gpsSource.shareLocation')}
                          </span>
                          <select
                            aria-label={t('gpsSource.shareLocation')}
                            value={displayedGpsSourceValue}
                            onChange={(event) => updatePendingGpsSourceValue(event.target.value)}
                            disabled={gpsSourceOptionsLoading || isGpsSourceSwitching}
                            className={cn('w-full min-w-0 max-w-full sm:w-auto px-3 py-2 rounded border border-gray-300 dark:border-gray-600', background.secondary, text.primary)}
                          >
                            <option value="off">{t('gpsSource.off')}</option>
                            <option value="phone">{t('gpsSource.phone')}</option>
                            {gpsDefaultDevice && (
                              <option value="default">
                                {t('gpsSource.defaultDevice', { deviceName: gpsDefaultDevice.device_name })}
                              </option>
                            )}
                            {otherGpsSourceDevices.map((device) => (
                              <option key={device.id} value={`device:${device.id}`}>
                                {device.device_name}
                              </option>
                            ))}
                            {typeof currentGpsSourceValue === 'string'
                              && currentGpsSourceValue.startsWith('device:')
                              && !hasCurrentGpsSourceDeviceOption && (
                              <option value={currentGpsSourceValue} disabled>
                                {gpsSourceOptionsError ? t('gpsSource.loadFailed') : t('gpsSource.otherDevice')}
                              </option>
                            )}
                          </select>
                        </label>
                        <button
                          type="button"
                          onClick={() => handleInProgressGpsSourceChange(displayedGpsSourceValue)}
                          disabled={gpsSourceApplyDisabled}
                          className={cn('px-3 py-2 rounded flex shrink-0 items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed', components.button.primary)}
                        >
                          <Check className="h-4 w-4" />
                          {t('apply')}
                        </button>
                        {gpsSourceOptionsError && (
                          <span className={cn('w-full text-xs text-red-600 dark:text-red-300')}>
                            {t('gpsSource.loadFailed')}
                          </span>
                        )}
                      </div>
                    )}
                    {!showGpsSourceChooser && gpsSourceOptionsError && (
                      <span className={cn('w-full text-xs text-red-600 dark:text-red-300')}>
                        {t('gpsSource.loadFailed')}
                      </span>
                    )}
                  </div>
                )}
                {memoizedTripData.operational_unavailability_warning?.affected && (
                  <div className="w-full rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-100">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{t('operationalUnavailability.existingTripWarning')}</span>
                    </div>
                  </div>
                )}
                <div className={cn(
                  "px-4 py-2 rounded-lg shadow flex items-center gap-2",
                  background.secondary
                )}>
                  {selectedGpsFeedActive ? (
                    <>
                      <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      <span className={cn("text-sm", text.primary)}>{t('GPS Active')}</span>
                    </>
                  ) : locationSharingEnabled ? (
                    <>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                      <span className={cn("text-sm", text.primary)}>{t('gpsSource.status.waitingForFeed')}</span>
                    </>
                  ) : (
                    <>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                      <span className={cn("text-sm", text.primary)}>{t('GPS Inactive')}</span>
                    </>
                  )}
                </div>
                {gpsError && !isCompletingTrip && !isCompleteActionPending && memoizedTripData.status !== 'completed' && (
                  <div className={cn(
                    "w-full mt-2 p-2 rounded-lg text-sm",
                    "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
                  )}>{gpsError}</div>
                )}
                {showGpsSourceConflict && (
                  <div className={cn(
                    'w-full rounded border p-3 text-sm',
                    currentGpsSourceConflict.isBlocking
                      ? 'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-100'
                      : 'bg-blue-50 border-blue-300 text-blue-900 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-100'
                  )}>
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium">
                          {t('gpsSource.conflict.title')}
                        </div>
                        <div className="mt-1">
                          {currentGpsSourceConflict.isBlocking
                            ? t('gpsSource.conflict.blockingMessage')
                            : t('gpsSource.conflict.warningMessage')}
                        </div>
                        {conflictTrips.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {conflictTrips.map((trip, index) => (
                              <li key={trip.tripId || index} className="break-words">
                                <span className="font-medium">{trip.routeName || t('gpsSource.conflict.unknownTrip')}</span>
                                {trip.status ? (
                                  <>
                                    <span aria-hidden="true"> - </span>
                                    <span>{trip.status}</span>
                                  </>
                                ) : null}
                                {trip.driverName ? (
                                  <>
                                    <span aria-hidden="true"> - </span>
                                    <span>{trip.driverName}</span>
                                  </>
                                ) : null}
                                {trip.vehicleName ? (
                                  <>
                                    <span aria-hidden="true"> - </span>
                                    <span>{trip.vehicleName}</span>
                                  </>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => chooseGpsConflictRecovery('phone')}
                            className={cn('px-3 py-1.5 rounded', components.button.secondary)}
                          >
                            {t('gpsSource.conflict.usePhone')}
                          </button>
                          <button
                            type="button"
                            onClick={() => chooseGpsConflictRecovery('off')}
                            className={cn('px-3 py-1.5 rounded', components.button.secondary)}
                          >
                            {t('gpsSource.conflict.turnOff')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowGpsSourceChooser(true)}
                            className={cn('px-3 py-1.5 rounded', components.button.secondary)}
                          >
                            {t('gpsSource.conflict.selectAnotherDevice')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="px-6 pb-4">
              <div className="flex flex-wrap items-center gap-2">
                <TripActions
                  status={memoizedTripData.status}
                  onStartTrip={handleStartTripRequest}
                  onCompleteTrip={() => setShowCompleteDialog(true)}
                  onCancelTrip={() => setShowCancelDialog(true)}
                  onCreateReferenceRoute={handleCreateReferenceRouteClick}
                  isAdmin={isAdmin}
                  isCompletingTrip={isCompletingTrip || isCompleteActionPending}
                />
                {canLaunchWaypointReorder({
                  trip: memoizedTripData,
                  status: memoizedTripData.status,
                  role: currentEntity?.userRole,
                  userId: localStorage.getItem('userId')
                }) && (
                  <button
                    onClick={() => setShowReorderModal(true)}
                    className={cn(
                      "px-4 py-2 rounded flex items-center gap-2",
                      "bg-indigo-500 text-white hover:bg-indigo-600"
                    )}
                  >
                    <ListOrdered className="h-4 w-4" />
                    {t('reorderStops')}
                  </button>
                )}
                {canManageOperationalDelay && (
                  <button
                    type="button"
                    onClick={() => setShowOperationalDelayDialog(true)}
                    className={cn(
                      "px-4 py-2 rounded flex items-center gap-2",
                      "bg-amber-500 text-white hover:bg-amber-600"
                    )}
                  >
                    <Clock className="h-4 w-4" />
                    {t('operationalDelay.actionButton')}
                  </button>
                )}
              </div>
            </div>
            <StartConfirmDialog
              isOpen={showStartDialog}
              onClose={() => setShowStartDialog(false)}
              availableTypes={memoizedTripData?.available_archived_types}
              defaultType={(pendingStartArchivedType ?? memoizedTripData?.selected_archived_type) || 'representative'}
              vehicleOptions={startVehicleOptions}
              selectedVehicleId={selectedStartVehicleId}
              onVehicleChange={handleStartVehicleChange}
              vehicleConflict={startVehicleConflict}
              requiresVehicleSelection={startVehicleSelectionRequired}
              gpsReadinessRequired={startGpsReadiness.required}
              gpsReadinessReady={startGpsReadiness.ready}
              gpsReadinessMessageKey={startGpsReadiness.messageKey}
              onConfirm={handleStartDialogConfirm}
            />
            <CancelConfirmDialog
              isOpen={showCancelDialog}
              onClose={() => setShowCancelDialog(false)}
              onConfirm={(reason) => {
                wrappedCancelTrip(reason);
                setShowCancelDialog(false);
              }}
            />
            <CompleteConfirmDialog
              isOpen={showCompleteDialog}
              onClose={() => setShowCompleteDialog(false)}
              onConfirm={() => {
                wrappedCompleteTrip();
                setShowCompleteDialog(false);
              }}
              isLoading={isCompletingTrip || isCompleteActionPending}
              isDisabled={isCompletingTrip || isCompleteActionPending}
            />
            <SaveShortcutSegmentsDialog
              isOpen={showShortcutSegmentsDialog}
              onClose={handleShortcutSegmentsClose}
              entityId={currentEntity?.entityId}
              tripId={memoizedTripData?.id}
              routeId={shortcutEdgesData?.routeId || memoizedTripData?.route_id}
            />
            <ReferenceRouteDialog
              isOpen={showReferenceRouteDialog}
              onClose={() => setShowReferenceRouteDialog(false)}
              onConfirm={handleCreateReferenceRoute}
              existingTypes={existingReferenceTypes}
              routeId={memoizedTripData?.route_id}
              entityId={currentEntity?.entityId}
              tripId={memoizedTripData?.id}
              loading={referenceRouteLoading}
            />
            <TripWaypointReorderModal
              isOpen={showReorderModal}
              onClose={() => setShowReorderModal(false)}
              entityId={currentEntity?.entityId}
              tripId={memoizedTripData?.id}
              tripInfo={{
                routeId: memoizedTripData?.route_id,
                routeName: memoizedTripData?.route_name,
                tripDate: memoizedTripData?.trip_date,
                departureTime: memoizedTripData?.departure_time
              }}
              onOrderChanged={(payload) => refreshAfterWaypointReorder(payload, { source: 'modal' })}
            />
            <OperationalDelayDialog
              isOpen={showOperationalDelayDialog && canManageOperationalDelay}
              onClose={() => setShowOperationalDelayDialog(false)}
              entityId={currentEntity?.entityId}
              tripId={memoizedTripData?.id}
              onChanged={(result) => handleTripBookingsChanged({
                ...(result || {}),
                eventType: 'trip_delay_updated'
              })}
            />
            <WaypointCorrectionDialog
              isOpen={correctionDialogState.isOpen}
              onClose={handleCorrectionDialogClose}
              waypointName={correctionDialogState.waypointName}
              coLocatedWaypoints={correctionDialogState.coLocatedWaypoints}
              onConfirm={handleCorrectionDialogConfirm}
              isLoading={isCorrectionDialogLoading}
            />
          </>
        )}
        {hasOperationalDelayAccess
          && effectiveEtaSource !== 'none'
          && memoizedTripData.status === 'canceled' && (
          <div className="px-6 pb-4 pt-4">
            <button
              type="button"
              disabled
              title={t('operationalDelay.canceledDisabled')}
              className="flex cursor-not-allowed items-center gap-2 rounded bg-gray-400 px-4 py-2 text-white opacity-60 dark:bg-gray-600"
            >
              <Clock className="h-4 w-4" />
              {t('operationalDelay.actionButton')}
            </button>
          </div>
        )}
        <div className={cn(isRiderView ? "py-4 sm:p-6" : "p-6")}>
          {entityTimeZoneState.status === 'ready' && entityTimeZoneState.differsFromBrowser && (
            <div
              role="status"
              className={cn(
                'mx-4 mb-4 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200 sm:mx-0'
              )}
            >
              <Clock className="h-4 w-4 flex-shrink-0" />
              <span>{t('tripTimesUseTimeZone', { zoneLabel: entityTimeZoneState.label })}</span>
            </div>
          )}
          {entityTimeZoneState.status === 'unavailable' && (
            <div
              role="alert"
              className="mx-4 mb-4 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100 sm:mx-0"
            >
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{t('tripTimezoneUnavailable')}</span>
            </div>
          )}
          {showRiderTripSummary && (
            <RiderTripSummarySection
              displayEtaData={displayEtaData}
              connectionStatus={connectionStatus}
              isStalePresentation={frozenLiveEtaSnapshot !== null}
              isAwaitingLiveEtaResume={isAwaitingLiveEtaResume}
              riderScopedTripData={riderScopedTripData}
              gpsActive={gpsActive}
              isDriver={isDriver}
              userPickupStopId={userPickupStopId}
              userDropoffStopId={userDropoffStopId}
              currentTripPlan={currentTripPlan}
              showWaypoints={showWaypoints}
              waypointStates={waypointStates}
              entitySettings={entitySettings}
              riderBookings={riderBookings}
              selectedRiderBooking={selectedRiderBooking}
              onSelectBooking={handleSelectRiderBooking}
              riderContentInsetClassName={riderContentInsetClassName}
              entityTimeZone={entityTimeZoneState.timeZone}
            />
          )}
          <div className={cn(
            "relative w-full overflow-hidden z-0",
            isDriver ? "h-96 rounded-lg mb-4" : "h-80 rounded-none mb-4 sm:rounded-lg"
          )}>
            {formatRoute && (
                <TripTrackingMap
                  selectedRoute={formatRoute}
                  mapCenter={initialMapCenter}
                  mapZoom={14}
                  showDefaultMarker={false}
                  entityAddress=""
                  t={t}
                  pickingMode={false}
                  onLocationPick={() => {}}
                  onMapChange={() => {}}
                  entityChangeRef={{ current: false }}
                  currentLocation={currentLocation}
                  gpsTrail={gpsTrail}
                  tripData={riderScopedTripData}
                  currentEntity={currentEntity}
                  etaData={displayEtaData}
                  onForceRefresh={handleForceRefresh}
                  isrefreshing={isRefreshing}
                  isDriver={isDriver}
                  userPickupStopId={userPickupStopId}
                  userDropoffStopId={userDropoffStopId}
                  filterWaypointsWithoutActivity={isDriver && !showWaypoints}
                  planRefreshTrigger={planRefreshTrigger}
                  latestPlanUpdate={latestPlanUpdate}
                  onPlanStateChange={handlePlanStateChange}
                  onLocalTripPlanUpdated={handleLocalTripPlanUpdated}
                />
            )}
          </div>
          <div className={cn("mb-4", riderContentInsetClassName)}>
            <ETADisplay
              etaData={displayEtaData}
              isLoading={false}
              error={null}
              tripData={riderScopedTripData}
              currentEntity={currentEntity}
              isDriver={isDriver}
              gpsActive={gpsActive}
              entitySettings={entitySettings}
              waypointStates={waypointStates}
              canManualOverride={canManualOverride}
              canManualSkip={canManualSkip}
              onManualArrival={handleManualArrival}
              onManualDeparture={handleManualDeparture}
              onManualSkip={handleManualSkip}
              onManualCorrection={handleManualCorrection}
              onPreTripManualCorrection={handlePreTripManualCorrection}
              connectionStatus={connectionStatus}
              showWaypoints={showWaypoints}
              setShowWaypoints={setShowWaypoints}
              tripPlan={currentTripPlan}
              entityTimeZone={entityTimeZoneState.timeZone}
            />
          </div>
          {memoizedTripData.status === 'dispatched' && isDriver && (
            <div className="mb-4">
              <BoardingDisplay
                tripData={memoizedTripData}
                onStartTrip={handleStartTripRequest}
              />
            </div>
          )}
          {/* Boarding Status Dashboard - Show for drivers during dispatched, in_progress, and completed trips
              entitySettings?.boarding_tracking_enabled validates BOTH via getEntityPreferences:
              1. Global is_active = 1 in preference_definitions (filtered server-side)
              2. Entity-specific boarding_tracking_enabled = true */}
          {entitySettings?.boarding_tracking_enabled && isDriver
            && (memoizedTripData.status === 'in_progress' || memoizedTripData.status === 'dispatched' || memoizedTripData.status === 'completed') && (
            <div className="mb-4">
              <BoardingStatusDashboard
                tripId={memoizedTripData.id}
                entityId={currentEntity.entityId}
                refreshTrigger={boardingStatusRefreshTrigger}
                onStatusUpdate={(data) => {
                  logger.debug('Boarding status updated', {
                    tripId: memoizedTripData.id,
                    entityId: currentEntity.entityId,
                    hasData: Boolean(data),
                    status: data?.status || null
                  });
                  // Optionally refresh trip data here if needed
                }}
              />
            </div>
          )}
          <div className={cn("grid grid-cols-1 md:grid-cols-4 gap-4 mb-4", riderContentInsetClassName)}>
            {isDriver && (
              <div className={cn("p-4 rounded-lg", background.secondary)}>
                <div className="flex items-center gap-2">
                  <Navigation2 className="h-5 w-5 text-blue-500" />
                  <div>
                    <h3 className={cn("font-medium", text.primary)}>{t('speed')}</h3>
                    <p className={text.secondary}>{formatSpeed(locationInfo.speed)}</p>
                  </div>
                </div>
              </div>
            )}
            {tripData.status === 'in_progress' && (
              <div className={cn("p-4 rounded-lg", background.secondary)}>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-500" />
                  <div>
                    <h3 className={cn("font-medium", text.primary)}>{t('last_updated')}</h3>
                    <p className={text.secondary}>
                      {locationInfo.lastUpdate
                        ? (entityTimeZoneState.status === 'ready'
                            ? formatTime(locationInfo.lastUpdate, timeFormat, entityTimeZoneState.timeZone)
                            : '--:--')
                        : (locationInfo.lastUpdateInvalid ? '--:--' : t('N/A'))}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Riders get the plain-language connection strip on the summary
                card instead; the technical tile stays for driver/admin. */}
            {(isDriver || isAdmin) && (
              <div className={cn("p-4 rounded-lg", background.secondary)}>
                <ConnectionStatus />
              </div>
            )}
          </div>
          {/* Rider Boarding Pass Section - Only for riders (non-drivers) with bookings
              entitySettings?.boarding_tracking_enabled validates BOTH via getEntityPreferences:
              1. Global is_active = 1 in preference_definitions (filtered server-side)
              2. Entity-specific boarding_tracking_enabled = true */}
          {!isDriver && selectedRiderBooking && entitySettings?.boarding_tracking_enabled && (
            <div className={cn(
              "p-4 mb-4 flex items-center justify-center",
              riderContentInsetClassName
            )}>
              
                {/*<div>
                  <h3 className={cn("text-lg font-semibold mb-1", text.primary)}>
                    {t('yourBooking')}
                  </h3>
                  <p className={cn("text-sm", text.secondary)}>
                    {t('From')}: {tripData.user_booking.pickup_stop_name} → {t('To')}: {tripData.user_booking.dropoff_stop_name}
                  </p>
                  <p className={cn("text-sm", text.secondary)}>
                    {tripData.user_booking.riders} {t('riders')} | {tripData.user_booking.luggages} {t('luggage')}
                  </p>
                </div> */}
                <button
                  onClick={handleViewBoardingPass}
                  className={cn(
                    'px-6 py-3 rounded-md text-sm font-medium flex items-center gap-2',
                    'bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700',
                    'transition-colors duration-200 shadow-sm hover:shadow-md'
                  )}
                >
                  <QrCode className="h-5 w-5" />
                  {t('viewBoardingPass')}
                </button>
              
            </div>
          )}

          {/* Trip Details */}
          {isDriver && (
            <div className={cn(
              "border rounded-lg p-4",
              "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
            )}>
              <div className="grid grid-cols-3 gap-4">
                
                <div className={cn("p-4 rounded-lg", background.secondary)}>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-500" />
                    <div>
                      <h3 className={cn("font-medium", text.primary)}>{t('Trip Date')}</h3>
                      <p className={text.secondary}>{formatDate(tripData.trip_date, dateFormat)}</p>
                    </div>
                  </div>
                </div>
                <div className={cn("p-4 rounded-lg", background.secondary)}>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-blue-500" />
                    <div>
                      <h3 className={cn("font-medium", text.primary)}>{t('Departure Time at Start Location')}</h3>
                      <p className={text.secondary}>
                        {entityScheduledDeparture && entityTimeZoneState.status === 'ready'
                          ? formatTime(entityScheduledDeparture, timeFormat, entityTimeZoneState.timeZone)
                          : '--:--'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className={cn("p-4 rounded-lg", background.secondary)}>
                  <div className="flex items-center gap-2">
                    <Car className="h-5 w-5 text-blue-500" />
                    <div>
                      <h3 className={cn("font-medium", text.primary)}>{t('Vehicle')}</h3>
                      <p className={text.secondary}>{tripData.vehicle_name || t('Not Available')}</p>
                    </div>
                  </div>
                </div>
                <div className={cn("p-4 rounded-lg", background.secondary)}>
                  <div className="flex items-center gap-2">
                    <User className="h-5 w-5 text-blue-500" />
                    <div>
                      <h3 className={cn("font-medium", text.primary)}>{t('Driver')}</h3>
                      <p className={text.secondary}>{tripData.driver_name || t('Not Available')}</p>
                    </div>
                  </div>
                </div>
                <div className={cn("p-4 rounded-lg", background.secondary)}>
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-blue-500" />
                    <div>
                      <h3 className={cn("font-medium", text.primary)}>{t('Total Bookings')}</h3>
                      <p className={text.secondary}>{tripData.total_bookings || t('Not Available')}</p>
                    </div>
                  </div>
                </div>
                <div className={cn("p-4 rounded-lg", background.secondary)}>
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-500" />
                    <div>
                      <h3 className={cn("font-medium", text.primary)}>{t('Passengers')}</h3>
                      <p className={text.secondary}>{tripData.total_riders || 0}</p>
                    </div>
                  </div>
                </div>
                <div className={cn("p-4 rounded-lg", background.secondary)}>
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-blue-500" />
                    <div>
                      <h3 className={cn("font-medium", text.primary)}>{t('Luggages')}</h3>
                      <p className={text.secondary}>{tripData.total_luggages || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Boarding Pass Modal */}
      {selectedBoardingBooking && (
        <BoardingPassModal
          open={boardingPassModalOpen}
          onClose={() => {
            setBoardingPassModalOpen(false);
            setSelectedBoardingBooking(null);
          }}
          booking={selectedBoardingBooking}
          entityId={currentEntity?.entityId}
        />
      )}
    </div>
  );
};

export {
  clearPreTripStaticEstimate,
  stripOperationalDelayFromEstimatedState
};
export default TripTracker;
